import { useState, useEffect, useRef } from "react";
import { supabase } from './supabase';
import { getGreeting } from './greetings';
import BroadcastPage from './components/Broadcast';
import CalendarPage from './components/Calendar';
import SettingsPage from './components/Settings';
import { RUNDOWN_TYPES } from './components/project/RunDown';
import { ProjectDetail } from './components/project/ProjectDetail';
import { logActivity, UserActivityFeed, ActivityPage } from './components/ActivityLog'; // eslint-disable-line no-unused-vars
import { RepertoirePage } from './components/Repertoire';
import { PortalsPage } from './components/Portals';
import { CustomersPage, CustomerSelect } from './components/Customers';
import { OffersPage } from './components/Offers';



const C = {
  bg:"#0e0e0e",sidebar:"#0a0a0a",surface:"#181818",surface2:"#202020",
  border:"#2a2a2a",muted:"#666",orange:"#FF5F1F",magenta:"#E8197D",
  gold:"#F5A623",green:"#4ade80",white:"#fff",red:"#f87171",blue:"#60a5fa",
};

function useIsMobile() {
  const [mobile,setMobile]=useState(()=>typeof window!=="undefined"&&window.innerWidth<680);
  useEffect(()=>{
    const fn=()=>setMobile(window.innerWidth<680);
    window.addEventListener("resize",fn);
    return ()=>window.removeEventListener("resize",fn);
  },[]);
  return mobile;
}





const isPast = p => { const d=p.end_date||p.date; return d && new Date(d+"T23:59:59") < new Date(); };
// Datovisning for eventer: "12. mars" eller "12. mars – 14. mars" for flerdagers
const fmtRange = (p, opts={day:"numeric",month:"short"}) => {
  if(!p?.date) return "";
  const f = d => new Date(d+"T12:00:00").toLocaleDateString("nb-NO", opts);
  return p.end_date && p.end_date!==p.date ? `${f(p.date)} – ${f(p.end_date)}` : f(p.date);
};

// eslint-disable-next-line no-unused-vars
const MODULE_CFG = {
  rehearsals:   { label:"Øvingstider",         icon:"🕐", adminOnly:false },
  showprogram:  { label:"Show-program",         icon:"🎭", adminOnly:false },
  travel:       { label:"Reise og transport",   icon:"✈️", adminOnly:false },
  accommodation:{ label:"Overnatting",          icon:"🏨", adminOnly:false },
  costume:      { label:"Kostyme",              icon:"👗", adminOnly:false },
  contract:     { label:"Kontrakt og betaling", icon:"📋", adminOnly:true  },
};

const statusCfg = {
  confirmed:  { label:"Bekreftet",   color:C.green,   bg:"#14532d44", border:"#4ade8033" },
  inquiry:    { label:"Forespørsel", color:C.magenta, bg:"#E8197D22", border:"#E8197D44" },
  cancelled:  { label:"Kansellert",  color:C.red,     bg:"#3d151522", border:"#f8717133" },
};
const inviteCfg = {
  accepted:{ label:"Akseptert", color:C.green },
  pending: { label:"Venter",    color:C.gold  },
  declined:{ label:"Avvist",    color:C.red   },
};
const runsheetTypes = RUNDOWN_TYPES;

const roleCfg = {
  admin:      { label:"Admin",      color:"#34d399" },
  danser:     { label:"Danser",     color:C.magenta },
  vokalist:   { label:"Vokalist",   color:"#a78bfa" },
  tm:         { label:"TM",         color:C.gold    },
  akrobat:    { label:"Akrobat",    color:C.orange  },
  musiker:    { label:"Musiker",    color:C.blue    },
  tekniker:   { label:"Tekniker",   color:"#94a3b8" },
};

const card    = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const badge   = s => ({ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:500, color:statusCfg[s].color, background:statusCfg[s].bg, border:`0.5px solid ${statusCfg[s].border}`, whiteSpace:"nowrap" });
const ibadge  = s => ({ padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:500, color:inviteCfg[s].color, background:`${inviteCfg[s].color}22`, border:`0.5px solid ${inviteCfg[s].color}44` });
const rbadge  = r => ({ padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:500, color:roleCfg[r]?.color||C.muted, background:`${roleCfg[r]?.color||C.muted}22`, border:`0.5px solid ${roleCfg[r]?.color||C.muted}44` });
const inp     = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:16, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn     = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white,border:"none"}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="success"?{background:"#14532d",color:C.green,border:`0.5px solid #4ade8033`}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...(v==="warn"?{background:"#3d2e0a",color:C.gold,border:`0.5px solid ${C.gold}44`}:{}), ...x });
const lbl     = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;
const secTitle= t => <div style={{fontSize:11,color:C.muted,marginBottom:12,letterSpacing:0.5,textTransform:"uppercase"}}>{t}</div>;
const mkInitials = name => name.trim().split(" ").filter(Boolean).slice(0,2).map(w=>w[0].toUpperCase()).join("");

const defaultPermissions = {
  seeAllProjects: false,
  createProjects: false,
  editOwnProjects: false,
  editAllProjects: false,
  invitePeople: false,
  seeAdminNotes: false,
};

function can(user, permission) {
  if(user?.role === "admin") return true;
  return user?.permissions?.[permission] === true;
}

function createNotification(type,project,recipientIds,extra={}) {
  return { type, project_id:project.id, project_name:project.name, recipient_ids:recipientIds, read:{}, ...extra };
}

function Avatar({ initials, size=36, role }) {
  const color=roleCfg[role]?.color||C.orange;
  return <div style={{width:size,height:size,borderRadius:"50%",background:`${color}22`,border:`1px solid ${color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size>30?13:10,color,fontWeight:500,flexShrink:0}}>{initials}</div>;
}
function AvatarStack({ userIds, users }) {
  return (
    <div style={{display:"flex"}}>
      {userIds.slice(0,3).map((uid,i)=>{const u=users.find(x=>x.id===uid);const color=roleCfg[u?.role]?.color||C.orange;return <div key={uid} style={{width:24,height:24,borderRadius:"50%",background:`${color}22`,border:`1px solid ${C.sidebar}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color,fontWeight:500,marginLeft:i>0?-6:0}}>{u?.initials?.slice(0,1)}</div>;})}
      {userIds.length>3&&<div style={{width:24,height:24,borderRadius:"50%",background:C.surface2,border:`1px solid ${C.sidebar}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:C.muted,marginLeft:-6}}>+{userIds.length-3}</div>}
    </div>
  );
}
function Toggle({ on, onClick }) {
  return <div onClick={onClick} style={{width:40,height:22,borderRadius:11,background:on?C.orange:"#333",border:`0.5px solid ${on?C.orange:C.border}`,position:"relative",cursor:"pointer",flexShrink:0,transition:"background 0.2s"}}><div style={{position:"absolute",top:3,left:on?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/></div>;
}
function Toast({ msg, ok=true }) {
  if(!msg) return null;
  return <div style={{position:"fixed",bottom:72,left:"50%",transform:"translateX(-50%)",background:C.surface,border:`0.5px solid ${ok?C.green:C.red}44`,color:ok?C.green:C.red,padding:"10px 20px",borderRadius:20,fontSize:13,zIndex:400,whiteSpace:"nowrap"}}>{msg}</div>;
}
function useToast() {
  const [toast,setToast]=useState({msg:"",ok:true});
  const show=(msg,ok=true)=>{ setToast({msg,ok}); setTimeout(()=>setToast({msg:"",ok:true}),2500); };
  return [toast,show];
}

function SearchBar({ projects, users, user, onSelectProject, onSelectUser, isMobile }) {
  const [q,setQ]=useState("");
  const [open,setOpen]=useState(false);
  const ref=useRef(null);

  useEffect(()=>{
    const fn=e=>{ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",fn);
    return ()=>document.removeEventListener("mousedown",fn);
  },[]);

  const sq=q.trim().toLowerCase();
  const matchedProjects=sq.length<2?[]:projects.filter(p=>
    user.role==="admin"||(p.team.includes(user.id)&&p.invites?.[user.id]!=="declined")
  ).filter(p=>
    p.name?.toLowerCase().includes(sq)||
    p.location?.toLowerCase().includes(sq)||
    p.client?.toLowerCase().includes(sq)
  ).slice(0,5);
  const matchedUsers=sq.length<2||user.role!=="admin"?[]:users.filter(u=>
    u.name?.toLowerCase().includes(sq)||
    u.email?.toLowerCase().includes(sq)
  ).slice(0,4);

  const hasResults=matchedProjects.length>0||matchedUsers.length>0;

  return (
    <div ref={ref} style={{position:"relative",width:"100%"}}>
      <div style={{position:"relative"}}>
        <svg style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}} width="13" height="13" viewBox="0 0 16 16" fill="none" stroke={C.muted} strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10 10l3 3"/></svg>
        <input
          style={{...inp({paddingLeft:30,fontSize:12}),background:C.surface}}
          placeholder="Søk..."
          value={q}
          onChange={e=>{setQ(e.target.value);setOpen(true);}}
          onFocus={()=>setOpen(true)}
        />
        {q&&<button onClick={()=>{setQ("");setOpen(false);}} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>}
      </div>
{open&&q.length>=2&&<div style={{position:"absolute",top:"calc(100% + 6px)",left:0,width:320,background:C.surface,border:`0.5px solid ${C.border}`,borderRadius:10,zIndex:500,overflow:"hidden",boxShadow:"0 8px 32px rgba(0,0,0,0.4)"}}>
        {!hasResults&&<div style={{padding:"14px 16px",fontSize:13,color:C.muted}}>Ingen resultater for «{q}»</div>}
        {matchedProjects.length>0&&<>
          <div style={{padding:"8px 14px 4px",fontSize:10,color:C.muted,textTransform:"uppercase",letterSpacing:0.5}}></div>
          {matchedProjects.map(p=>(
            <div key={p.id} onClick={()=>{onSelectProject(p);setQ("");setOpen(false);}} style={{padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,borderTop:`0.5px solid ${C.border}`}}
              onMouseEnter={e=>e.currentTarget.style.background=C.surface2}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{width:28,height:28,borderRadius:6,background:`${C.orange}22`,border:`0.5px solid ${C.orange}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:C.orange,flexShrink:0}}>📁</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{p.location} · {fmtRange(p,{day:"numeric",month:"short",year:"numeric"})}</div>
              </div>
              <div style={badge(p.status)}>{statusCfg[p.status]?.label}</div>
            </div>
          ))}
        </>}
        {matchedUsers.length>0&&<>
          <div style={{padding:"8px 14px 4px",fontSize:10,color:C.muted,textTransform:"uppercase",letterSpacing:0.5}}>Brukere</div>
          {matchedUsers.map(u=>(
            <div key={u.id} onClick={()=>{onSelectUser(u);setQ("");setOpen(false);}} style={{padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,borderTop:`0.5px solid ${C.border}`}}
              onMouseEnter={e=>e.currentTarget.style.background=C.surface2}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Avatar initials={u.initials} size={28} role={u.role}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500}}>{u.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{u.email}</div>
              </div>
              <span style={rbadge(u.role)}>{roleCfg[u.role]?.label}</span>
            </div>
          ))}
        </>}
      </div>}
    </div>
  );
}

function ProjectCard({ p, users, onClick, onRespond, currentUser }) {
  const isPending=currentUser&&p.invites?.[currentUser.id]==="pending";
  const showCount=(p.modules?.showprogram_data||[]).length;
  const statusColor={confirmed:C.green,inquiry:C.magenta,cancelled:C.red}[p.status]||C.muted;
  return (
    <div style={{...card(),marginBottom:8,cursor:"pointer",border:`0.5px solid ${isPending?C.gold:C.border}`,paddingLeft:0,overflow:"hidden",display:"flex"}}
      onMouseEnter={e=>e.currentTarget.style.borderColor=isPending?C.gold:C.orange}
      onMouseLeave={e=>e.currentTarget.style.borderColor=isPending?C.gold:C.border}>
      {/* Farget venstrekant */}
      <div style={{width:3,background:statusColor,flexShrink:0,borderRadius:"10px 0 0 10px"}}/>
      <div style={{flex:1,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}} onClick={onClick}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:4}}>{p.name}</div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                <div style={{fontSize:12,color:C.muted}}>📅 {fmtRange(p)}</div>
                {p.location&&<div style={{fontSize:12,color:C.muted}}>📍 {p.location}</div>}
                {p.client&&<div style={{fontSize:12,color:C.muted}}>🏢 {p.client}</div>}
                {showCount>0&&<div style={{fontSize:12,color:C.muted}}>🎭 {showCount} show</div>}
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              <AvatarStack userIds={p.team} users={users}/>
              <div style={badge(p.status)}>{statusCfg[p.status].label}</div>
            </div>
          </div>
        {isPending&&onRespond&&(
          <div style={{display:"flex",gap:8,marginTop:10,paddingTop:10,borderTop:`0.5px solid ${C.border}`}}>
            <button style={btn("success",{flex:1,padding:"6px 0",fontSize:12})} onClick={e=>{e.stopPropagation();onRespond(p,"accepted");}}>✓ Aksepter</button>
            <button style={btn("danger",{flex:1,padding:"6px 0",fontSize:12})} onClick={e=>{e.stopPropagation();onRespond(p,"declined");}}>✗ Avvis</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Notifications ──
function NotifBell({ notifications, user, onOpen }) {
  const unread=notifications.filter(n=>n.recipient_ids.includes(user.id)&&!n.read[user.id]).length;
  return (
    <div onClick={onOpen} style={{position:"relative",cursor:"pointer",width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:unread?`${C.orange}22`:"transparent",border:`0.5px solid ${unread?C.orange:C.border}`,flexShrink:0}}>
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={unread?C.orange:C.muted} strokeWidth="1.5"><path d="M8 1a5 5 0 015 5v3l1.5 2H1.5L3 9V6a5 5 0 015-5z"/><path d="M6.5 13a1.5 1.5 0 003 0"/></svg>
      {unread>0&&<div style={{position:"absolute",top:-4,right:-4,minWidth:16,height:16,borderRadius:8,background:C.orange,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#fff",padding:"0 3px"}}>{unread}</div>}
    </div>
  );
}
function NotifPanel({ notifications, user, projects, onMarkRead, onClose, onSelectProject, onRespond, isMobile }) {
  const [broadcastModal, setBroadcastModal] = useState(null);
  const mine=notifications.filter(n=>(n.recipient_ids||[]).includes(user.id)).sort((a,b)=>new Date(b.created_at||b.ts+"Z")-new Date(a.created_at||a.ts+"Z"));
  const fmtTime=ts=>{ if(!ts) return ""; const d=new Date(ts+"Z"),now=new Date(),diff=Math.floor((now-d)/60000); if(isNaN(diff)) return ""; if(diff<1) return "Akkurat nå"; if(diff<60) return `${diff}m siden`; if(diff<1440) return `${Math.floor(diff/60)}t siden`; return d.toLocaleDateString("nb-NO",{day:"numeric",month:"short"}); };
  const icon=t=>t==="confirmed"?"✓":t==="updated"?"↻":t==="removed"?"✕":t==="feedback_fixed"?"✓":t==="broadcast"?"📢":"✉";
  const color=t=>t==="confirmed"?C.green:t==="updated"?C.blue:t==="removed"?C.red:t==="feedback_fixed"?C.green:t==="broadcast"?C.orange:C.gold;
  const text=n=>{ if(n.type==="broadcast") return `${n.broadcast_title||"Ny melding"}${n.broadcast_message?` — ${n.broadcast_message}`:""}`; const name=n.project_name||n.projectName||"Ukjent event"; if(n.type==="confirmed") return `«${name}» er nå bekreftet!`; if(n.type==="updated") return `«${name}» er oppdatert${n.fields?" ("+n.fields+")":""}`; if(n.type==="removed") return `Du er fjernet fra «${name}»`; if(n.type==="feedback_fixed") return `Din tilbakemelding er markert som fikset${n.feedback_message?`: «${n.feedback_message}${n.feedback_message.length>=60?"...":""}»`:""}`; return `Du er invitert til «${name}»`; };
  const panelStyle=isMobile?{position:"fixed",inset:0,background:C.sidebar,zIndex:300,overflowY:"auto",paddingTop:"env(safe-area-inset-top)"}:{position:"fixed",top:0,left:240,width:320,height:"100vh",background:C.sidebar,borderRight:`0.5px solid ${C.border}`,overflowY:"auto",zIndex:300};
  return (
    <div style={{position:"fixed",inset:0,zIndex:299}} onClick={onClose}>
      <div style={panelStyle} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"20px 16px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`0.5px solid ${C.border}`}}>
          <div style={{fontSize:15,fontWeight:500}}>Varsler</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {mine.some(n=>!n.read[user.id])&&<button style={btn("ghost",{padding:"4px 10px",fontSize:11})} onClick={()=>mine.forEach(n=>onMarkRead(n.id))}>Merk alle lest</button>}
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",lineHeight:1}}>×</button>
          </div>
        </div>
        {broadcastModal&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:16}} onClick={()=>setBroadcastModal(null)}>
  <div style={{background:C.surface,borderRadius:12,padding:24,width:"100%",maxWidth:480,border:`0.5px solid ${C.border}`}} onClick={e=>e.stopPropagation()}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
      <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1}}>Melding fra {broadcastModal.broadcast_sender}</div>
      <button onClick={()=>setBroadcastModal(null)} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer"}}>×</button>
    </div>
    <div style={{fontSize:18,fontWeight:600,marginBottom:12}}>{broadcastModal.broadcast_title}</div>
    <div style={{fontSize:14,color:"#ccc",lineHeight:1.7,whiteSpace:"pre-wrap",marginBottom:broadcastModal.project_name?16:0}}>{broadcastModal.broadcast_message}</div>
    {broadcastModal.project_name&&<div style={{fontSize:12,color:C.orange,padding:"8px 12px",background:`${C.orange}11`,borderRadius:8,border:`0.5px solid ${C.orange}33`}}>🎭 Knyttet til «{broadcastModal.project_name}»</div>}
  </div>
</div>}
        {mine.length===0&&<div style={{padding:32,textAlign:"center",color:C.muted,fontSize:13}}>Ingen varsler enda.</div>}
        {mine.map(n=>{
          const unread=!n.read[user.id];
          const proj=projects?.find(p=>p.id===(n.project_id||n.projectId));
          const dato=proj?.date?new Date(proj.date+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"long",year:"numeric"}):"";
          const inviteStatus=proj?.invites?.[user.id];
          return (
            <div key={n.id} style={{padding:"12px 16px",borderBottom:`0.5px solid ${C.border}`,background:unread?"#ffffff08":"transparent",display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{width:28,height:28,borderRadius:"50%",background:`${color(n.type)}22`,border:`0.5px solid ${color(n.type)}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:color(n.type),flexShrink:0}}>{icon(n.type)}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:unread?C.white:"#aaa",lineHeight:1.4,cursor:"pointer"}} onClick={()=>{ onMarkRead(n.id); if(n.type==="broadcast") { setBroadcastModal(n); } else if(proj) { onSelectProject(n.project_id||n.projectId); onClose(); }}}>{text(n)}</div>
                {n.type==="invited"&&dato&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{dato}</div>}
                {n.type==="invited"&&proj&&inviteStatus==="pending"&&(
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button style={btn("success",{padding:"4px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();onRespond(proj,user.id,"accepted");onMarkRead(n.id);}}>✓ Aksepter</button>
                    <button style={btn("danger",{padding:"4px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();onRespond(proj,user.id,"declined");onMarkRead(n.id);}}>✗ Avvis</button>
                  </div>
                )}
                {n.type==="invited"&&inviteStatus==="accepted"&&<div style={{fontSize:11,color:C.green,marginTop:6}}>✓ Du har akseptert</div>}
                {n.type==="invited"&&inviteStatus==="declined"&&<div style={{fontSize:11,color:C.red,marginTop:6}}>✗ Du har avvist</div>}
                <div style={{fontSize:11,color:C.muted,marginTop:4}}>{fmtTime(n.created_at||n.ts)}</div>
              </div>
              {unread&&<div style={{width:6,height:6,borderRadius:"50%",background:C.orange,marginTop:6,flexShrink:0}}/>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Forgot Password ──
function ForgotPasswordScreen({ onBack }) {
  const [email,setEmail]=useState("");
  const [sent,setSent]=useState(false);
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);

  const send = async () => {
    if(!email){ setErr("Skriv inn e-postadressen din."); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://app.royal.show"
    });
    if(error){ setErr("Kunne ikke sende e-post. Sjekk adressen og prøv igjen."); setLoading(false); return; }
    setSent(true);
    setLoading(false);
  };

  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:C.bg,padding:16,fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div style={{width:"100%",maxWidth:360}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <img src="/logo.svg" alt="Royal" style={{width:200,marginBottom:16}}/>
          <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1}}/>
          <div style={{fontSize:13,color:C.muted,marginTop:10}}>Tilbakestill passord</div>
        </div>
        <div style={card({padding:24})}>
          {sent
            ? <div style={{textAlign:"center"}}>
                <div style={{fontSize:24,color:C.green,marginBottom:12}}>✓</div>
                <div style={{fontSize:14,color:C.white,marginBottom:8}}>Sjekk e-posten din!</div>
                <div style={{fontSize:13,color:C.muted,marginBottom:20}}>Vi har sendt en lenke til {email}</div>
                <button style={{...btn("ghost"),width:"100%"}} onClick={onBack}>Tilbake til innlogging</button>
              </div>
            : <>
                {lbl("E-postadresse")}
                <input style={inp({marginBottom:err?8:20})} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="din@epost.no" onKeyDown={e=>e.key==="Enter"&&send()}/>
                {err&&<div style={{fontSize:12,color:C.magenta,marginBottom:12}}>{err}</div>}
                <button style={{...btn("primary"),width:"100%",padding:12,fontSize:14,marginBottom:8}} onClick={send} disabled={loading}>{loading?"Sender...":"Send tilbakestillingslenke"}</button>
                <button style={{...btn("ghost"),width:"100%",padding:10,fontSize:13}} onClick={onBack}>← Tilbake til innlogging</button>
              </>}
        </div>
      </div>
    </div>
  );
}

// ── Reset Password ──
function ResetPasswordScreen({ onDone }) {
  const [newPw,setNewPw]=useState("");
  const [confPw,setConfPw]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [done,setDone]=useState(false);

  const save = async () => {
    if(newPw.length<6){ setErr("Passordet må være minst 6 tegn."); return; }
    if(newPw!==confPw){ setErr("Passordene stemmer ikke."); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    if(error){ setErr(error.message); setLoading(false); return; }
    setDone(true);
    setTimeout(()=>onDone(), 2000);
  };

  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:C.bg,padding:16,fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div style={{width:"100%",maxWidth:360}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <img src="/logo.svg" alt="Royal" style={{width:200,marginBottom:16}}/>
          <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1}}/>
          <div style={{fontSize:13,color:C.muted,marginTop:10}}>Sett nytt passord</div>
        </div>
        <div style={card({padding:24})}>
          {done
            ? <div style={{textAlign:"center",color:C.green,fontSize:14}}>✓ Passord endret! Logger inn...</div>
            : <>
              {lbl("Nytt passord")}
              <input style={inp({marginBottom:14})} type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Minst 6 tegn"/>
              {lbl("Bekreft passord")}
              <input style={inp({marginBottom:err?8:20})} type="password" value={confPw} onChange={e=>setConfPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&save()}/>
              {err&&<div style={{fontSize:12,color:C.magenta,marginBottom:12}}>{err}</div>}
              <button style={{...btn("primary"),width:"100%",padding:12,fontSize:14}} onClick={save} disabled={loading}>{loading?"Lagrer...":"Sett nytt passord"}</button>
            </>}
        </div>
      </div>
    </div>
  );
}

// ── Login ──
function LoginScreen({ onLogin }) {
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [forgotPassword,setForgotPassword]=useState(false);
  const [remember,setRemember]=useState(true);

  if(forgotPassword) return <ForgotPasswordScreen onBack={()=>setForgotPassword(false)}/>;

const go = async () => {
  setLoading(true);
  const { data, error } = await supabase.auth.signInWithPassword({ email, password: pw, options:{ persistSession: remember } });
  if (error) { setErr("Feil e-post eller passord."); setLoading(false); return; }
  const { data: userData } = await supabase.from('users').select('*').eq('email', data.user.email).single();
  if (userData && !userData.active) {
    await supabase.auth.signOut();
    setErr("Denne brukeren er deaktivert. Kontakt administrator.");
    setLoading(false);
    return;
  }
 if(userData) {
    await supabase.from('users').update({last_seen: new Date().toISOString()}).eq('id', userData.id);
    await logActivity(userData, 'login', {}, 'login');
    await onLogin({...userData, last_seen: new Date().toISOString()});
  }
  setLoading(false);
};

  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:C.bg,padding:16,fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div style={{width:"100%",maxWidth:360}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <img src="/logo.svg" alt="Royal" style={{width:200,marginBottom:16}}/>
          <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1}}/>
          <div style={{fontSize:13,color:C.muted,marginTop:10}}>Backstage</div>
        </div>
        <div style={card({padding:24})}>
          {lbl("E-post")}<input style={inp({marginBottom:14})} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="E-postadresse" onKeyDown={e=>e.key==="Enter"&&go()}/>
          {lbl("Passord")}<input style={inp({marginBottom:err?8:20})} type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&go()}/>
          {err&&<div style={{fontSize:12,color:C.magenta,marginBottom:12}}>{err}</div>}
          <button style={{...btn("primary"),width:"100%",padding:12,fontSize:14,marginBottom:8}} onClick={go} disabled={loading}>{loading?"Logger inn...":"Logg inn"}</button>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,cursor:"pointer"}} onClick={()=>setRemember(v=>!v)}>
            <div style={{width:18,height:18,borderRadius:4,border:`1.5px solid ${remember?C.orange:C.border}`,background:remember?C.orange:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {remember&&<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>}
            </div>
            <div style={{fontSize:13,color:C.muted,userSelect:"none"}}>Husk meg</div>
          </div>
          <button style={{...btn("ghost"),width:"100%",padding:10,fontSize:13}} onClick={()=>setForgotPassword(true)}>Glemt passord?</button>
        </div>
      </div>
    </div>
  );
}

// ── Toggle component ──
function UserModal({ existing, onSave, onClose, isMobile }) {
    const [d,setD]=useState(existing||{name:"",email:"",password:"",role:"danser",active:true});
  const [inviteMode,setInviteMode]=useState(!existing);
  const set=(k,v)=>setD(x=>({...x,[k]:v}));
  const save=()=>{ if(!d.name||!d.email) return; onSave({...d,initials:mkInitials(d.name),inviteMode}); };
  const modalStyle = isMobile
    ? {position:"fixed",inset:0,background:C.surface,overflowY:"auto",padding:20,zIndex:200}
    : {position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200};
  const innerStyle = isMobile ? {} : {background:C.surface,borderRadius:12,padding:28,width:440,border:`0.5px solid ${C.border}`};
  return (
    <div style={modalStyle} onClick={isMobile?undefined:onClose}>
      <div style={innerStyle} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:18,fontWeight:500}}>{existing?"Rediger bruker":"Ny bruker"}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
        </div>
        <div style={{marginBottom:14}}>{lbl("Fullt navn *")}<input style={inp()} value={d.name} onChange={e=>set("name",e.target.value)} placeholder="Fornavn Etternavn"/></div>
        <div style={{marginBottom:14}}>{lbl("E-post *")}<input style={inp()} type="email" value={d.email} onChange={e=>set("email",e.target.value)}/></div>
{!existing&&<div style={{marginBottom:14}}>
          {lbl("Tilgang")}
          <div style={{display:"flex",gap:8,marginTop:4}}>
            <div onClick={()=>setInviteMode(true)} style={{flex:1,padding:"8px 0",borderRadius:8,textAlign:"center",cursor:"pointer",fontSize:13,border:`0.5px solid ${inviteMode?C.orange:C.border}`,background:inviteMode?"#FF5F1F22":"transparent",color:inviteMode?C.orange:C.muted}}>✉️ Send invitasjon</div>
            <div onClick={()=>setInviteMode(false)} style={{flex:1,padding:"8px 0",borderRadius:8,textAlign:"center",cursor:"pointer",fontSize:13,border:`0.5px solid ${!inviteMode?C.orange:C.border}`,background:!inviteMode?"#FF5F1F22":"transparent",color:!inviteMode?C.orange:C.muted}}>🔑 Sett passord</div>
          </div>
        </div>}
        {!inviteMode&&<div style={{marginBottom:14}}>{lbl("Passord")}<input style={inp()} type="text" value={d.password} onChange={e=>set("password",e.target.value)} placeholder="Minst 6 tegn"/></div>}
        {inviteMode&&!existing&&<div style={{marginBottom:14,padding:"10px 12px",background:`${C.orange}11`,borderRadius:8,border:`0.5px solid ${C.orange}22`}}><div style={{fontSize:12,color:C.muted}}>Brukeren mottar en e-post med lenke for å sette sitt eget passord.</div></div>}
        <div style={{marginBottom:20}}>{lbl("Rolle")}
          <div style={{display:"flex",gap:8,marginTop:4}}>
            {Object.entries(roleCfg).map(([k,v])=>(
              <div key={k} onClick={()=>set("role",k)} style={{flex:1,padding:"8px 0",borderRadius:8,textAlign:"center",cursor:"pointer",fontSize:13,border:`0.5px solid ${d.role===k?v.color:C.border}`,background:d.role===k?`${v.color}22`:"transparent",color:d.role===k?v.color:C.muted}}>{v.label}</div>
            ))}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button style={btn("ghost")} onClick={onClose}>Avbryt</button>
          <button style={btn("primary")} onClick={save}>{existing?"Lagre":"Legg til"}</button>
        </div>
      </div>
    </div>
  );
}



// ── Dashboard ──
function Dashboard({ user, projects, users, onSelect, onRespond, isMobile }) {
  const isAdmin = user.role==="admin";
  const visible = isAdmin ? projects : projects.filter(p=>p.team.includes(user.id)&&p.invites?.[user.id]==="accepted"&&!isPast(p));
  const upcoming = [...visible].filter(p=>!isPast(p)).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const next = upcoming[0];
  const nextDate = next?.date;
  const sameDay = nextDate ? upcoming.filter(p=>p.date===nextDate) : [];
  const pending = user.role!=="admin"?projects.filter(p=>p.team.includes(user.id)&&p.invites?.[user.id]==="pending").length:0;

  const daysUntil = p => {
    const diff = new Date(p.date+"T12:00:00") - new Date();
    const days = Math.ceil(diff/(1000*60*60*24));
    if(days<0) return "Pågår nå!";
    if(days===0) return "I dag!";
    if(days===1) return "I morgen!";
    return `Om ${days} dager`;
  };

  // Hent alle øvinger fra aksepterte prosjekter
  const allRehearsals = projects
    .filter(p=>p.team.includes(user.id)&&(isAdmin||(p.invites?.[user.id]==="accepted"))&&(p.modules||{}).rehearsals)
    .flatMap(p=>(p.modules?.rehearsals_data||[]).map(r=>({...r,projectName:p.name,projectId:p.id,project:p})))
    .filter(r=>r.date&&new Date(r.date+"T23:59:59")>=new Date())
    .sort((a,b)=>new Date(a.date+"T"+a.timeFrom)-new Date(b.date+"T"+b.timeFrom))
    .slice(0,5);

  if(isAdmin) return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{marginBottom:24,padding:"8px 0 16px"}}>
        <div style={{fontSize:10,color:`${C.orange}88`,letterSpacing:2,textTransform:"uppercase",marginBottom:4}}>Royal Backstage</div>
<div style={{fontSize:13,color:C.muted,marginBottom:6,letterSpacing:1,textTransform:"uppercase"}}>{getGreeting(user,projects)}</div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:isMobile?20:24,fontWeight:600,lineHeight:1.2}}>
            <span style={{background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{user.name.split(" ")[0]}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={rbadge(user.role)}>{roleCfg[user.role]?.label}</span>
            <Avatar initials={user.initials} role={user.role}/>
          </div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:12,marginBottom:20}}>
        {[
{label:"Eventer",val:projects.filter(p=>!p.archived&&!isPast(p)).length,sub:`${projects.filter(p=>p.status==="confirmed"&&!isPast(p)).length} bekreftet`},          {label:"Forespørsler",val:projects.filter(p=>p.status==="inquiry").length,sub:"ikke bekreftet"},
          {label:"Neste oppdrag",val:next?fmtRange(next):"–",sub:next?.location?.split(",")[0]||""},
        ].map((s,i)=>i===2&&isMobile?null:(
          <div key={s.label} style={card()}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:20,fontWeight:500}}>{s.val}</div>
            <div style={{fontSize:11,color:C.orange,marginTop:4}}>{s.sub}</div>
          </div>
        ))}
        {isMobile&&next&&<div style={{...card(),gridColumn:"1/-1"}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:6}}>Neste oppdrag</div>
          <div style={{fontSize:16,fontWeight:500}}>{fmtRange(next)}</div>
          <div style={{fontSize:11,color:C.orange,marginTop:4}}>{next.location}</div>
        </div>}
      </div>
      {next&&<div style={{marginBottom:20}}>
        {secTitle(sameDay.length>1?"Neste oppdrag":"Neste oppdrag")}
        {sameDay.map(p=>(
        <div key={p.id} style={{...card({border:`0.5px solid ${C.orange}33`,background:"#141414",marginBottom:10}),cursor:"pointer",overflow:"hidden"}} onClick={()=>onSelect(p)}>
          <div style={{height:3,background:`linear-gradient(90deg,${C.orange},${C.magenta})`,borderRadius:2,marginBottom:16,marginLeft:-16,marginRight:-16,marginTop:-16}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:4}}>{p.name}</div>
              <div style={{fontSize:13,color:C.muted}}>{p.client}</div>
            </div>
            <div style={badge(p.status)}>{statusCfg[p.status].label}</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3,textTransform:"uppercase",letterSpacing:0.5}}>Dato</div>
              <div style={{fontSize:14,fontWeight:500}}>{fmtRange(p,{day:"numeric",month:"long",year:"numeric"})}</div>
              <div style={{fontSize:12,color:C.orange,marginTop:2}}>{daysUntil(p)}</div>
            </div>
            <div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3,textTransform:"uppercase",letterSpacing:0.5}}>Sted</div>
              <div style={{fontSize:14,fontWeight:500}}>{p.location||"–"}</div>
              {p.address&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>{p.address}</div>}
              {p.country&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>{p.country}</div>}
            </div>
          </div>
        </div>
        ))}
      </div>}
      {allRehearsals.length>0&&<div style={{marginBottom:20}}>
        {secTitle("Kommende øvinger")}
        {allRehearsals.map((r,i)=>(
          <div key={i} style={{...card({marginBottom:8,cursor:"pointer"}),display:"flex",alignItems:"center",gap:12}} onClick={()=>onSelect(r.project)}>
            <div style={{width:36,height:36,borderRadius:8,background:`${C.magenta}22`,border:`0.5px solid ${C.magenta}44`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <div style={{fontSize:9,color:C.magenta,textTransform:"uppercase"}}>{new Date(r.date+"T12:00:00").toLocaleDateString("nb-NO",{month:"short"})}</div>
              <div style={{fontSize:15,fontWeight:600,color:C.magenta,lineHeight:1}}>{new Date(r.date+"T12:00:00").getDate()}</div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,marginBottom:2}}>{r.projectName}</div>
              <div style={{fontSize:12,color:C.muted}}>{r.timeFrom}{r.timeTo?` – ${r.timeTo}`:""}{r.location?` · ${r.location}`:""}</div>
              {r.comment&&<div style={{fontSize:11,color:"#555",marginTop:2}}>{r.comment}</div>}
            </div>
          </div>
        ))}
      </div>}
      {upcoming.filter(p=>p.date!==nextDate).length>0&&<>
        {secTitle("Kommende eventer")}
        {upcoming.filter(p=>p.date!==nextDate).slice(0,4).map(p=><ProjectCard key={p.id} p={p} users={users} onClick={()=>onSelect(p)}/>)}
      </>}
      {isAdmin&&(()=>{
        const fmtDate=d=>new Date(d+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short"});
        const isOverdue=d=>d&&new Date(d+"T23:59:59")<new Date();
        const isDueSoon=d=>{ if(!d) return false; const diff=new Date(d+"T23:59:59")-new Date(); return diff>0&&diff<3*24*60*60*1000; };
        const upcomingTodos=projects
          .filter(p=>!p.archived)
          .flatMap(p=>(p.todos||[]).map(t=>({...t,projectName:p.name,project:p})))
          .filter(t=>!t.done)
          .sort((a,b)=>{ if(!a.deadline&&!b.deadline) return 0; if(!a.deadline) return 1; if(!b.deadline) return -1; return new Date(a.deadline)-new Date(b.deadline); })
          .slice(0,5);
        if(upcomingTodos.length===0) return null;
        return (
          <div style={{marginTop:20}}>
            {secTitle("Neste to-dos")}
            {upcomingTodos.map((t,i)=>(
              <div key={i} style={{...card({marginBottom:8}),display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>onSelect(t.project)}>
                <div style={{width:18,height:18,borderRadius:4,border:`1.5px solid ${C.border}`,background:"transparent",flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:1}}>{t.projectName}{t.deadline&&<span style={{color:isOverdue(t.deadline)?C.red:isDueSoon(t.deadline)?C.gold:C.muted}}> · {fmtDate(t.deadline)}</span>}</div>
                </div>
                {t.deadline&&isOverdue(t.deadline)&&<div style={{fontSize:11,color:C.red,flexShrink:0}}>⚠ Utgått</div>}
                {t.deadline&&isDueSoon(t.deadline)&&!isOverdue(t.deadline)&&<div style={{fontSize:11,color:C.gold,flexShrink:0}}>⏰ Snart</div>}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );

  // ── Artist/Freelancer dashboard ──
  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {/* Hero */}
      <div style={{marginBottom:24,padding:"24px 0 20px"}}>
        <div style={{fontSize:10,color:`${C.orange}88`,letterSpacing:2,textTransform:"uppercase",marginBottom:4}}>Royal Backstage</div>
<div style={{fontSize:13,color:C.muted,marginBottom:6,letterSpacing:1,textTransform:"uppercase"}}>{getGreeting(user,projects)}</div>
        <div style={{fontSize:isMobile?20:24,fontWeight:600,lineHeight:1.2,marginBottom:10}}>
          <span style={{background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{user.name.split(" ")[0]}</span>
        </div>
        <span style={rbadge(user.role)}>{roleCfg[user.role]?.label}</span>
      </div>

      {/* Ventende invitasjoner */}
      {pending>0&&<div style={{...card({border:`0.5px solid ${C.gold}44`,marginBottom:20,display:"flex",alignItems:"center",gap:14})}}>
        <div style={{width:34,height:34,borderRadius:"50%",background:`${C.gold}22`,border:`1px solid ${C.gold}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:C.gold,flexShrink:0}}>{pending}</div>
        <div><div style={{fontSize:14,fontWeight:500}}>Du har {pending} ventende invitasjon{pending>1?"er":""}</div><div style={{fontSize:12,color:C.muted,marginTop:2}}>Gå til Invitasjoner for å svare</div></div>
      </div>}

      {/* Neste oppdrag */}
      {next&&<div style={{marginBottom:20}}>
        {secTitle("Neste oppdrag")}
        {sameDay.map(p=>(
        <div key={p.id} style={{...card({border:`0.5px solid ${C.orange}33`,background:"#141414",marginBottom:10}),cursor:"pointer",overflow:"hidden"}} onClick={()=>onSelect(p)}>
          <div style={{height:3,background:`linear-gradient(90deg,${C.orange},${C.magenta})`,borderRadius:2,marginBottom:16,marginLeft:-16,marginRight:-16,marginTop:-16}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:4}}>{p.name}</div>
              <div style={{fontSize:13,color:C.muted}}>{p.client}</div>
            </div>
            <div style={badge(p.status)}>{statusCfg[p.status].label}</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:p.runsheet?.length>0?16:0}}>
            <div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3,textTransform:"uppercase",letterSpacing:0.5}}>Dato</div>
              <div style={{fontSize:14,fontWeight:500}}>{fmtRange(p,{day:"numeric",month:"long",year:"numeric"})}</div>
              <div style={{fontSize:12,color:C.orange,marginTop:2}}>{daysUntil(p)}</div>
            </div>
            <div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3,textTransform:"uppercase",letterSpacing:0.5}}>Sted</div>
              <div style={{fontSize:14,fontWeight:500}}>{p.location||"–"}</div>
              {p.address&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>{p.address}</div>}
              {p.country&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>{p.country}</div>}
            </div>
          </div>
          {p.runsheet?.length>0&&<>
            <div style={{fontSize:10,color:C.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:0.5}}>Kjøreplan</div>
            {p.runsheet.slice(0,3).map(r=>(
              <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"7px 10px",background:C.surface2,borderRadius:6,borderLeft:`3px solid ${runsheetTypes[r.type]?.color||"#888"}`}}>
                <div style={{fontFamily:"monospace",fontSize:12,color:C.white,minWidth:40}}>{r.time}</div>
                <div style={{fontSize:12,color:"#ccc"}}>{r.title}</div>
              </div>
            ))}
            {p.runsheet.length>3&&<div style={{fontSize:11,color:C.muted,marginTop:4}}>+{p.runsheet.length-3} flere...</div>}
          </>}
        </div>
        ))}
      </div>}

      {/* Kommende øvinger */}
      {allRehearsals.length>0&&<div style={{marginBottom:20}}>
        {secTitle("Kommende øvinger")}
        {allRehearsals.map((r,i)=>(
          <div key={i} style={{...card({marginBottom:8,cursor:"pointer"}),display:"flex",alignItems:"center",gap:12}} onClick={()=>onSelect(r.project)}>
            <div style={{width:36,height:36,borderRadius:8,background:`${C.magenta}22`,border:`0.5px solid ${C.magenta}44`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <div style={{fontSize:9,color:C.magenta,textTransform:"uppercase"}}>{new Date(r.date+"T12:00:00").toLocaleDateString("nb-NO",{month:"short"})}</div>
              <div style={{fontSize:15,fontWeight:600,color:C.magenta,lineHeight:1}}>{new Date(r.date+"T12:00:00").getDate()}</div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,marginBottom:2}}>{r.projectName}</div>
              <div style={{fontSize:12,color:C.muted}}>{r.timeFrom}{r.timeTo?` – ${r.timeTo}`:""}{r.location?` · ${r.location}`:""}</div>
              {r.comment&&<div style={{fontSize:11,color:"#555",marginTop:2}}>{r.comment}</div>}
            </div>
          </div>
        ))}
      </div>}

      {/* Andre kommende oppdrag */}
      {upcoming.filter(p=>p.date!==nextDate).length>0&&<div style={{marginBottom:20}}>
        {secTitle("Andre kommende oppdrag")}
        {upcoming.filter(p=>p.date!==nextDate).slice(0,4).map(p=><ProjectCard key={p.id} p={p} users={users} onClick={()=>onSelect(p)} onRespond={onRespond} currentUser={user}/>)}
      </div>}

  {upcoming.length===0&&!pending&&<div style={{...card({textAlign:"center",padding:36,marginTop:20})}}>
        <div style={{fontSize:15,fontWeight:500,marginBottom:6}}>Ingen kommende oppdrag</div>
        <div style={{fontSize:13,color:C.muted}}>Du vil se oppdragene dine her når du blir invitert.</div>
      </div>}
    </div>
  );
}
// ── Projects ──
const PAGE_SIZE = 20;
function ProjectsPage({ user, projects, users, onSelect, onNew, isMobile }) {
  const [filter,setFilter]=useState("all");
  const [showArchived,setShowArchived]=useState(false);
  const [showPast,setShowPast]=useState(false);
  const [page,setPage]=useState(1);

  const base = can(user,"seeAllProjects")
    ? projects.filter(p=>showArchived?p.archived:!p.archived)
    : projects.filter(p=>p.team.includes(user.id)&&!p.archived&&p.invites?.[user.id]!=="declined");

  const upcoming = base.filter(p=>!isPast(p));
  const past = base.filter(p=>isPast(p));

  const activeList = filter==="all"?upcoming:upcoming.filter(p=>p.status===filter);
  const pastList = filter==="all"?past:past.filter(p=>p.status===filter);

  const paginated = activeList.slice(0, page*PAGE_SIZE);
  const hasMore = paginated.length < activeList.length;

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:20,fontWeight:500}}>{showArchived?"Arkiv":"Eventer"}</div>
        <div style={{display:"flex",gap:8}}>
          {user.role==="admin"&&<button style={btn("ghost",{fontSize:12})} onClick={()=>setShowArchived(v=>!v)}>{showArchived?"← Aktive":"Arkiv"}</button>}
          {can(user,"createProjects")&&!showArchived&&<button style={btn("primary")} onClick={onNew}>+ Nytt</button>}
        </div>
      </div>
      {!showArchived&&<div style={{display:"flex",gap:6,marginBottom:20,overflowX:"auto",paddingBottom:4}}>
        {["all","confirmed","inquiry","cancelled"].map(f=>(
          <button key={f} onClick={()=>{setFilter(f);setPage(1);}} style={{padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${filter===f?C.orange:C.border}`,background:filter===f?"#FF5F1F22":"transparent",color:filter===f?C.orange:C.muted,whiteSpace:"nowrap",flexShrink:0}}>
            {f==="all"?"Alle":statusCfg[f].label}
          </button>
        ))}
      </div>}
      {paginated.length===0&&<div style={{color:C.muted,fontSize:14,marginTop:40,textAlign:"center"}}>{showArchived?"Ingen arkiverte eventer.":"Ingen kommende eventer."}</div>}
      {(()=>{
        const groups={};
        paginated.forEach(p=>{
          const key=p.date?new Date(p.date+"T12:00:00").toLocaleDateString("nb-NO",{month:"long",year:"numeric"}):"Ukjent dato";
          if(!groups[key]) groups[key]=[];
          groups[key].push(p);
        });
        return Object.entries(groups).map(([month,ps])=>(
          <div key={month} style={{marginBottom:24}}>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10,fontWeight:600}}>{month}</div>
            {ps.map(p=><ProjectCard key={p.id} p={p} users={users} onClick={()=>onSelect(p)}/>)}
          </div>
        ));
      })()}
      {hasMore&&<button style={{...btn("ghost"),width:"100%",marginTop:8,padding:10,fontSize:13}} onClick={()=>setPage(p=>p+1)}>Last flere ({activeList.length - paginated.length} gjenstår)</button>}
      {!showArchived&&pastList.length>0&&<>
        <div style={{margin:"24px 0 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5}}>Gjennomførte ({pastList.length})</div>
          <button style={btn("ghost",{fontSize:11,padding:"3px 10px"})} onClick={()=>setShowPast(v=>!v)}>{showPast?"Skjul":"Vis"}</button>
        </div>
        {showPast&&pastList.map(p=>(
          <div key={p.id} style={{opacity:0.45}}>
            <ProjectCard p={p} users={users} onClick={()=>onSelect(p)}/>
          </div>
        ))}
      </>}
    </div>
  );
}

// ── New project modal ──
function NewProjectModal({ users, customers, onCreateCustomer, onSave, onClose, isMobile }) {
  const [d,setD]=useState({name:"",date:"",end_date:null,location:"",client:"",customer_id:null,status:"inquiry",description:"",notes:"",team:[],runsheet:[],files:[],invites:{},modules:{showprogram:true}});
  const set=(k,v)=>setD(x=>({...x,[k]:v}));
  const toggle=id=>set("team",d.team.includes(id)?d.team.filter(x=>x!==id):[...d.team,id]);
  const [saving,setSaving]=useState(false);
  const save = async () => {
  if(!d.name||!d.date||saving) return;
  if(d.end_date&&d.end_date<d.date) return;
  setSaving(true);
  try {
    const year = new Date(d.date).getFullYear();
    const { data } = await supabase.from('projects').select('id').order('id');
    const numbers = (data||[]).map(p => parseInt(p.id.split("-")[2])).filter(n=>!isNaN(n));
    const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 100;
    const id = `R-${year}-${next}`;
    const invites = {};
    d.team.forEach(uid=>{invites[uid]="pending";});
    await onSave({...d, id, invites});
  } catch(e) {
    console.error("Feil ved oppretting av event:",e);
  } finally {
    setSaving(false);
  }
};
  const modalStyle=isMobile?{position:"fixed",inset:0,background:C.surface,overflowY:"auto",padding:20,zIndex:100}:{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100};
  const innerStyle=isMobile?{}:{background:C.surface,borderRadius:12,padding:28,width:520,maxHeight:"85vh",overflowY:"auto",border:`0.5px solid ${C.border}`};
  return (
    <div style={modalStyle} onClick={isMobile?undefined:onClose}>
      <div style={innerStyle} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:18,fontWeight:500}}>Nytt event</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
        </div>
        <div style={{marginBottom:14}}>{lbl("Navn på event *")}<input style={inp()} value={d.name} onChange={e=>set("name",e.target.value)} placeholder="Navn på produksjon"/></div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:14}}>
          <div>{lbl("Dato *")}<input style={inp()} type="date" value={d.date} onChange={e=>set("date",e.target.value)}/>{lbl("Til dato (flerdagers)")}<input style={inp({marginTop:2})} type="date" min={d.date||undefined} value={d.end_date||""} onChange={e=>set("end_date",e.target.value||null)}/>{d.end_date&&d.end_date<d.date&&<div style={{fontSize:11,color:C.red,marginTop:4}}>Sluttdato må være etter startdato</div>}</div>
          <div>{lbl("Sted")}<input style={inp()} value={d.location} onChange={e=>set("location",e.target.value)} placeholder="Venue / sted"/><input style={inp({marginTop:6})} value={d.address||""} onChange={e=>set("address",e.target.value)} placeholder="Adresse"/><input style={inp({marginTop:6})} value={d.country||""} onChange={e=>set("country",e.target.value)} placeholder="Land"/></div>
        </div>
        <div style={{marginBottom:14}}>{lbl("Kunde")}<CustomerSelect customers={customers||[]} value={d.customer_id} onCreate={onCreateCustomer}
          onChange={id=>{ set("customer_id",id); set("client",(customers||[]).find(c=>c.id===id)?.name||""); }}/></div>
        <div style={{marginBottom:14}}>{lbl("Status")}<select style={inp()} value={d.status} onChange={e=>set("status",e.target.value)}>{Object.entries(statusCfg).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select></div>
        <div style={{marginBottom:14}}>{lbl("Beskrivelse")}<textarea style={inp({minHeight:60,resize:"vertical"})} value={d.description} onChange={e=>set("description",e.target.value)}/></div>
        <div style={{marginBottom:20}}>
          {lbl("Velg team")}
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
            {users.filter(u=>u.active).map(u=>(
              <div key={u.id} onClick={()=>toggle(u.id)} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:20,cursor:"pointer",border:`0.5px solid ${d.team.includes(u.id)?roleCfg[u.role]?.color:C.border}`,background:d.team.includes(u.id)?`${roleCfg[u.role]?.color}22`:"transparent",color:d.team.includes(u.id)?roleCfg[u.role]?.color:C.muted,fontSize:13}}>
                {u.name.split(" ")[0]}
              </div>
            ))}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button style={btn("ghost")} onClick={onClose}>Avbryt</button>
          <button style={btn("primary")} onClick={save} disabled={saving}>{saving?"Oppretter...":"Opprett event"}</button>
        </div>
      </div>
    </div>
  );
}



// ── Invitations ──
function InvitationsPage({ user, projects, users, onUpdate, onSelectProject, isMobile }) {
  const myPending=projects.filter(p=>p.team.includes(user.id)&&p.invites?.[user.id]==="pending");
  const handled  =projects.filter(p=>p.team.includes(user.id)&&["accepted","declined"].includes(p.invites?.[user.id]));
  const fmt=p=>fmtRange(p,{day:"numeric",month:"long",year:"numeric"});
  const [acting,setActing]=useState(null);
  const respond=(project,decision)=>{ setActing(project.id+decision); setTimeout(()=>{ onUpdate({...project,invites:{...project.invites,[user.id]:decision}}); setActing(null); },600); };
  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{fontSize:20,fontWeight:500,marginBottom:20}}>Invitasjoner</div>
      {myPending.length===0&&<div style={{...card({textAlign:"center",padding:36,marginBottom:20})}}>
        <div style={{fontSize:24,marginBottom:10,color:C.green}}>✓</div>
        <div style={{fontSize:14,color:C.muted}}>Ingen ventende invitasjoner</div>
      </div>}
      {myPending.length>0&&<>{secTitle("Venter på ditt svar")}{myPending.map(p=>(
        <div key={p.id} style={{...card({marginBottom:16,border:`0.5px solid ${C.gold}44`})}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
            <div style={{flex:1,minWidth:0,marginRight:10}}>
              <div style={{fontSize:11,color:"#555",fontFamily:"monospace",marginBottom:3}}>#{p.id}</div>
              <div style={{fontSize:15,fontWeight:500,marginBottom:3}}>{p.name}</div>
              <div style={{fontSize:12,color:C.muted}}>{fmt(p)}</div>
              <div style={{fontSize:12,color:C.muted}}>{p.location}</div>
            </div>
            <div style={badge(p.status)}>{statusCfg[p.status].label}</div>
          </div>
          {p.description&&<div style={{fontSize:13,color:"#aaa",lineHeight:1.6,marginBottom:14,padding:"10px 12px",background:C.surface2,borderRadius:8}}>{p.description}</div>}
          {p.runsheet.length>0&&<div style={{marginBottom:14}}>
            {p.runsheet.slice(0,3).map(r=>(
              <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"8px 10px",background:C.surface2,borderRadius:6,borderLeft:`3px solid ${runsheetTypes[r.type]?.color||"#888"}`}}>
                <div style={{fontFamily:"monospace",fontSize:12,color:C.white,minWidth:40}}>{r.time}</div>
                <div style={{fontSize:12,color:"#ccc"}}>{r.title}</div>
              </div>
            ))}
            {p.runsheet.length>3&&<div style={{fontSize:11,color:C.muted,marginTop:3}}>+{p.runsheet.length-3} flere...</div>}
          </div>}
          <div style={{display:"flex",gap:8,flexWrap:isMobile?"wrap":"nowrap"}}>
            <button style={btn("success",{flex:1,padding:10,minWidth:isMobile?"100%":"auto"})} onClick={()=>respond(p,"accepted")} disabled={acting===p.id+"accepted"}>{acting===p.id+"accepted"?"...":"✓ Aksepter jobb"}</button>
            <button style={btn("danger",{flex:1,padding:10})}  onClick={()=>respond(p,"declined")} disabled={acting===p.id+"declined"}>{acting===p.id+"declined"?"...":"✗ Avvis"}</button>
            <button style={btn("ghost",{padding:"10px 14px"})} onClick={()=>onSelectProject(p)}>Detaljer</button>
          </div>
        </div>
      ))}</>}
      {handled.length>0&&<>{secTitle("Tidligere svar")}{handled.map(p=>{
        const isDeclined=p.invites[user.id]==="declined";
        return (
        <div key={p.id} style={{...card({display:"flex",alignItems:"center",gap:12,marginBottom:10,opacity:0.7,cursor:isDeclined?"default":"pointer"})}} onClick={isDeclined?undefined:()=>onSelectProject(p)}>
          <div style={{flex:1,minWidth:0}}><div style={{fontSize:14,fontWeight:500,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div><div style={{fontSize:12,color:C.muted}}>{fmt(p)}</div></div>
          <div style={ibadge(p.invites[user.id])}>{inviteCfg[p.invites[user.id]].label}</div>
        </div>
      );})}</>}
    </div>
  );
}

function AdminInvitationsPage({ projects, users, onUpdateProject, onSelectProject, isMobile }) {
  const [toast,showToast]=useToast();

  const allProjects = projects.map(p => {
    const members = Object.entries(p.invites||{}).map(([uid,st])=>({
      user: users.find(x=>x.id===uid),
      status: st,
      uid
    })).filter(x=>x.user);
    return { project:p, members };
  }).filter(x=>x.members.length>0);

  const total = allProjects.reduce((a,x)=>a+x.members.filter(m=>m.status==="pending").length,0);

  const setStatus=(project,uid,status)=>{
    const updated={...project,invites:{...project.invites,[uid]:status}};
    onUpdateProject(updated);
    showToast(status==="accepted"?"✓ Godkjent!":"✗ Avvist");
  };

  const remind=(name)=>showToast(`Påminnelse sendt til ${name.split(" ")[0]}!`);

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:500}}>Invitasjoner</div>
        <div style={{fontSize:13,color:C.gold,padding:"3px 10px",borderRadius:20,background:`${C.gold}22`,border:`0.5px solid ${C.gold}44`}}>{total} venter</div>
      </div>
      {allProjects.length===0&&<div style={{color:C.muted,fontSize:14,textAlign:"center",marginTop:40}}>Ingen aktive invitasjoner!</div>}
      {allProjects.map(({project:p,members})=>(
        <div key={p.id} style={{...card({marginBottom:16})}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,cursor:"pointer"}} onClick={()=>onSelectProject(p)}>
            <div>
              <div style={{fontSize:11,color:"#555",fontFamily:"monospace",marginBottom:3}}>#{p.id}</div>
              <div style={{fontSize:15,fontWeight:500}}>{p.name}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:2}}>{fmtRange(p,{day:"numeric",month:"long",year:"numeric"})}</div>
            </div>
            <div style={badge(p.status)}>{statusCfg[p.status].label}</div>
          </div>
          <div style={{borderTop:`0.5px solid ${C.border}`,paddingTop:12}}>
            {members.map(({user:u,status:st,uid})=>(
              <div key={uid} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"10px 12px",background:C.surface2,borderRadius:8,flexWrap:isMobile?"wrap":"nowrap"}}>
                <Avatar initials={u.initials} size={32} role={u.role}/>
                <div style={{flex:1,minWidth:80}}>
                  <div style={{fontSize:13,fontWeight:500}}>{u.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{roleCfg[u.role]?.label}</div>
                </div>
                <div style={ibadge(st)}>{inviteCfg[st].label}</div>
                {st==="pending"&&<div style={{display:"flex",gap:6}}>
                  <button style={btn("success",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();setStatus(p,uid,"accepted");}}>✓ Godkjenn</button>
                  <button style={btn("danger",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();setStatus(p,uid,"declined");}}>✗ Avvis</button>
                  <button style={btn("warn",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();remind(u.name);}}>Påminn</button>
                </div>}
                {st==="accepted"&&<button style={btn("danger",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();setStatus(p,uid,"declined");}}>Avvis</button>}
                {st==="declined"&&<button style={btn("success",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();setStatus(p,uid,"accepted");}}>✓ Godkjenn</button>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Team ──
function PermissionsPanel({ targetUser, onSave, onClose }) {
  const [perms, setPerms] = useState({...defaultPermissions, ...(targetUser.permissions||{})});
  const toggle = k => setPerms(p=>({...p,[k]:!p[k]}));
  const permLabels = {
    seeAllProjects:   "Se alle eventer",
    createProjects:   "Opprette eventer",
    editOwnProjects:  "Redigere egne eventer",
    editAllProjects:  "Redigere alle eventer",
    invitePeople:     "Invitere folk til eventer",
    seeAdminNotes:    "Se admin-notater",
  };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}}>
      <div style={{background:C.surface,borderRadius:12,padding:24,width:"100%",maxWidth:400,border:`0.5px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div>
            <div style={{fontSize:16,fontWeight:500}}>Rettigheter</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>{targetUser.name}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
        </div>
        {Object.entries(permLabels).map(([k,label])=>(
          <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:`0.5px solid ${C.border}`}}>
            <div style={{fontSize:13}}>{label}</div>
            <Toggle on={perms[k]} onClick={()=>toggle(k)}/>
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:20}}>
          <button style={{...btn("ghost"),flex:1}} onClick={onClose}>Avbryt</button>
          <button style={{...btn("primary"),flex:1}} onClick={()=>onSave(perms)}>Lagre</button>
        </div>
      </div>
    </div>
 );
}

// ── Team ──
function TeamPage({ user, users, onUpdateUsers, isMobile }) {
  const [modal,setModal]=useState(null);
  const [profile2,setProfile2]=useState(null);
  const [showPerms,setShowPerms]=useState(null);
  const [toast,showToast]=useToast();
const saveUser = async (u) => {
  if(u.id) {
    onUpdateUsers(users.map(x=>x.id===u.id?u:x));
    showToast("Bruker oppdatert.");
  } else if(u.inviteMode) {
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: { name:u.name, email:u.email, role:u.role, initials:u.initials, invite:true }
    });
    
   
    if(error) {
      const errText = await error.context?.json?.().catch(()=>null);
      showToast(errText?.error||error?.message||"Kunne ikke sende invitasjon.",false);
      setModal(null);
      return;
    }
    if(data?.error) { showToast(data.error||"Kunne ikke sende invitasjon.",false); setModal(null); return; }
    const newU = { ...u, id: data.user.id };
    onUpdateUsers([...users, newU]);
    showToast(`Invitasjon sendt til ${u.name.split(" ")[0]}!`);
    
  } else {
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: { name:u.name, email:u.email, password:u.password, role:u.role, initials:u.initials, invite:false }
    });
    if(error||data?.error) { showToast(data?.error||"Kunne ikke opprette bruker.",false); setModal(null); return; }
    const newU = { ...u, id: data.user.id };
    onUpdateUsers([...users, newU]);
    showToast(`${u.name} lagt til!`);
  }
  setModal(null);
};
  const toggleActive = async (u) => {
  await supabase.from('users').update({active:!u.active}).eq('id',u.id);
  onUpdateUsers(users.map(x=>x.id===u.id?{...x,active:!x.active}:x));
  showToast(u.active?`${u.name.split(" ")[0]} deaktivert.`:`${u.name.split(" ")[0]} aktivert.`);
};
if(user.role!=="admin") return null;
  const groups=[{key:"admin",label:"Administratorer"},{key:"danser",label:"Dansere"},{key:"vokalist",label:"Vokalister"},{key:"tm",label:"TM"},{key:"akrobat",label:"Akrobater"},{key:"musiker",label:"Musikere"},{key:"tekniker",label:"Teknikere"}];
  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {showPerms&&showPerms.role!=="admin"&&<PermissionsPanel targetUser={showPerms} onClose={()=>setShowPerms(null)} onSave={async(perms)=>{
        const updated = users.map(u=>u.id===showPerms.id?{...u,permissions:perms}:u);
        await onUpdateUsers(updated);
        setShowPerms(null);
      }}/>}
            {modal&&<UserModal existing={modal==="new"?null:modal} onSave={saveUser} onClose={()=>setModal(null)} isMobile={isMobile}/>}
      {profile2&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}} onClick={()=>setProfile2(null)}>
        <div style={{background:C.surface,borderRadius:12,padding:28,width:"100%",maxWidth:420,border:`0.5px solid ${C.border}`,maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}><Avatar initials={profile2.initials} size={38} role={profile2.role}/><div><div style={{fontSize:15,fontWeight:500}}>{profile2.name}</div><div style={{fontSize:12,color:C.muted}}>{profile2.email}</div></div></div>
            <button onClick={()=>setProfile2(null)} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
          </div>
          {(() => {
            const p=profile2.profile_info||{};
            const hasClothes=p.top||p.pants||p.shoes;
            const hasCostume=p.bust||p.waist||p.hips||p.inseam;
            const hasAny=p.phone||p.allergies||hasClothes||hasCostume;
            if(!hasAny) return <div style={{color:C.muted,fontSize:13,textAlign:"center",padding:20}}>Ingen profilinformasjon lagt til enda.</div>;
            return <>
             {p.phone&&<div style={{marginBottom:14}}>{lbl("Mobilnummer")}<div style={{fontSize:14,color:C.white}}>{p.phone}</div></div>}
              {p.address&&<div style={{marginBottom:14}}>{lbl("Adresse")}<div style={{fontSize:14,color:C.white}}>{p.address}</div></div>}
              {p.allergies&&<div style={{marginBottom:14}}>{lbl("Allergier")}<div style={{fontSize:14,color:C.white,lineHeight:1.6}}>{p.allergies}</div></div>}
              {(p.sas_eurobonus||p.norwegian_reward)&&<><div style={{fontSize:11,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Bonusprogram</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                {p.sas_eurobonus&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>SAS EUROBONUS</div><div style={{fontSize:14,fontWeight:500}}>{p.sas_eurobonus}</div></div>}
                {p.norwegian_reward&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>NORWEGIAN REWARD</div><div style={{fontSize:14,fontWeight:500}}>{p.norwegian_reward}</div></div>}
              </div></>}
              {hasClothes&&<><div style={{fontSize:11,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Klær</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
                {p.top&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>TOPP</div><div style={{fontSize:14,fontWeight:500}}>{p.top}</div></div>}
                {p.pants&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>BUKSE</div><div style={{fontSize:14,fontWeight:500}}>{p.pants}</div></div>}
                {p.shoes&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>SKO</div><div style={{fontSize:14,fontWeight:500}}>{p.shoes}</div></div>}
              </div></>}
              {hasCostume&&<><div style={{fontSize:11,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Kostymemål (cm)</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {p.bust&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>BYSTE</div><div style={{fontSize:14,fontWeight:500}}>{p.bust} cm</div></div>}
                {p.waist&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>MIDJE</div><div style={{fontSize:14,fontWeight:500}}>{p.waist} cm</div></div>}
                {p.hips&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>HOFTE</div><div style={{fontSize:14,fontWeight:500}}>{p.hips} cm</div></div>}
                {p.inseam&&<div style={{background:C.surface2,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.muted,marginBottom:4}}>INNSØM</div><div style={{fontSize:14,fontWeight:500}}>{p.inseam} cm</div></div>}
              </div></>}
            </>;
          })()}
          <div style={{height:"0.5px",background:C.border,margin:"20px 0"}}/>
          <div style={{fontSize:11,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Siste aktivitet</div>
          <UserActivityFeed userId={profile2.id} limit={8}/>
        </div>
      </div>}
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:500}}>Team</div>
        <button style={btn("primary")} onClick={()=>setModal("new")}>+ Ny bruker</button>
      </div>
      {groups.map(g=>{ const members=users.filter(u=>u.role===g.key); return (
        <div key={g.key} style={{marginBottom:24}}>
          {secTitle(g.label)}
          {members.map(u=>(
            <div key={u.id} style={{...card({marginBottom:10,opacity:u.active?1:0.5}),display:"flex",alignItems:"center",gap:12,flexWrap:isMobile?"wrap":"nowrap"}}>
              <Avatar initials={u.initials} size={38} role={u.role}/>
              <div style={{flex:1,minWidth:100}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                  <div style={{fontSize:14,fontWeight:500}}>{u.name}</div>
                  {!u.active&&<div style={{fontSize:11,padding:"1px 7px",borderRadius:20,color:C.red,border:`0.5px solid ${C.red}33`}}>Inaktiv</div>}
                </div>
                <div style={{fontSize:12,color:C.muted}}>{u.email}</div>
                {u.last_seen&&<div style={{fontSize:11,color:"#444",marginTop:2}}>Sist aktiv: {(()=>{ const d=new Date(u.last_seen),now=new Date(),diff=Math.floor((now-d)/60000); if(diff<1) return "Akkurat nå"; if(diff<60) return `${diff}m siden`; if(diff<1440) return `${Math.floor(diff/60)}t siden`; if(diff<10080) return `${Math.floor(diff/1440)}d siden`; return d.toLocaleDateString("nb-NO",{day:"numeric",month:"short"}); })()}</div>}
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
<button style={btn("ghost",{padding:"5px 10px",fontSize:11})} onClick={()=>setProfile2(u)}>Vis info</button>
{u.role!=="admin"&&<button style={btn("ghost",{padding:"5px 10px",fontSize:11})} onClick={()=>setShowPerms(u)}>Rettigheter</button>}
<button style={btn("ghost",{padding:"5px 10px",fontSize:11})} onClick={()=>setModal(u)}>Rediger</button>
{u.id!==1&&<button style={btn(u.active?"danger":"success",{padding:"5px 10px",fontSize:11})} onClick={()=>toggleActive(u)}>{u.active?"Deaktiver":"Aktiver"}</button>}
{u.id!==1&&!u.active&&<button style={btn("danger",{padding:"5px 10px",fontSize:11})} onClick={async()=>{ if(window.confirm(`Er du sikker på at du vil slette ${u.name}?`)){ await supabase.from('users').delete().eq('id',u.id); onUpdateUsers(users.filter(x=>x.id!==u.id)); showToast(`${u.name} slettet.`); }}}>Slett</button>}
              </div>
            </div>
          ))}
        </div>
      );})}
    </div>
  );
}

// ── Profile ──
function ProfilePage({ user, users, onUpdateUsers, isMobile }) {
const [name,setName]=useState(user.name);
  const [email,setEmail]=useState(user.email);
  const defaultProfile={phone:"",address:"",allergies:"",top:"",pants:"",shoes:"",bust:"",waist:"",hips:"",inseam:"",sas_eurobonus:"",norwegian_reward:""};
  const [profile,setProfile]=useState(user.profile_info||defaultProfile);
  const setP=(k,v)=>setProfile(p=>({...p,[k]:v}));
  const [,setCurPw]=useState(""); const [newPw,setNewPw]=useState(""); const [confPw,setConfPw]=useState("");
  const defaultPrefs={inApp:true,email:true,sms:false,onInvite:true,onConfirmed:true,onUpdated:false,onReminder:false};
  const [prefs,setPrefs]=useState(user.notif_prefs||user.notifPrefs||defaultPrefs);
  const togglePref=k=>setPrefs(p=>({...p,[k]:!p[k]}));
  const [toast,showToast]=useToast();
const saveProfile=async()=>{ 
  if(!name||!email){showToast("Navn og e-post er påkrevd.",false);return;} 
  const updated={...user,name,email,initials:mkInitials(name),profile_info:profile};
  const {error}=await supabase.from('users').update({name,email,initials:mkInitials(name),profile_info:profile}).eq('id',user.id);
  if(error){showToast("Kunne ikke lagre. Prøv igjen.",false);return;}
  onUpdateUsers(users.map(u=>u.id===user.id?updated:u));
  logActivity(user, 'profile_updated', {}, 'profile');
  showToast("Profil oppdatert!"); 
};
  const savePassword = async () => {
  if(newPw.length<6){showToast("Minst 6 tegn.",false);return;}
  if(newPw!==confPw){showToast("Passordene stemmer ikke.",false);return;}
  const { error } = await supabase.auth.updateUser({ password: newPw });
  if(error){ showToast("Feil: "+error.message,false); return; }
  setCurPw("");setNewPw("");setConfPw("");
  logActivity(user, 'password_changed', {}, 'profile');
  showToast("Passord endret!");
};
  const savePrefs=()=>{ 
  
  onUpdateUsers(users.map(u=>u.id===user.id?{...u,notif_prefs:prefs}:u)); 
  showToast("Innstillinger lagret!"); 
};
  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {gradBar}
      <div style={{fontSize:20,fontWeight:500,marginBottom:20}}>Min profil</div>
      <div style={{...card({display:"flex",alignItems:"center",gap:14,marginBottom:16})}}>
        <Avatar initials={mkInitials(name)||user.initials} size={52} role={user.role}/>
        <div><div style={{fontSize:15,fontWeight:500}}>{name}</div><div style={{fontSize:13,color:C.muted,marginTop:2}}>{email}</div><div style={{marginTop:6}}><span style={rbadge(user.role)}>{roleCfg[user.role]?.label}</span></div></div>
      </div>
      <div style={{...card(),marginBottom:16}}>
        {secTitle("Personlig informasjon")}
        <div style={{marginBottom:14}}>{lbl("Fullt navn")}<input style={inp()} value={name} onChange={e=>setName(e.target.value)}/></div>
        <div style={{marginBottom:16}}>{lbl("E-post")}<input style={inp()} type="email" value={email} onChange={e=>setEmail(e.target.value)}/></div>
        <div style={{marginBottom:16}}>{lbl("Mobilnummer")}<input style={inp()} type="tel" value={profile.phone} onChange={e=>setP("phone",e.target.value)} placeholder="+47 000 00 000"/></div>
        <div style={{marginBottom:16}}>{lbl("Adresse")}<input style={inp()} value={profile.address} onChange={e=>setP("address",e.target.value)} placeholder="Gateadresse, postnummer, sted"/></div>
        <div style={{marginBottom:16}}>{lbl("Allergier")}<textarea style={inp({minHeight:60,resize:"vertical"})} value={profile.allergies} onChange={e=>setP("allergies",e.target.value)} placeholder=""/></div>
        <div style={{height:"0.5px",background:C.border,margin:"4px 0 16px"}}/>
        <div style={{fontSize:11,color:C.muted,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Bonusprogram</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          <div>{lbl("SAS Eurobonus")}<input style={inp()} value={profile.sas_eurobonus} onChange={e=>setP("sas_eurobonus",e.target.value)} placeholder="EB-XXXXXXXX"/></div>
          <div>{lbl("Norwegian Reward")}<input style={inp()} value={profile.norwegian_reward} onChange={e=>setP("norwegian_reward",e.target.value)} placeholder="XXXXXXXX"/></div>
        </div>
        <div style={{height:"0.5px",background:C.border,margin:"4px 0 16px"}}/>
        <div style={{fontSize:11,color:C.muted,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Klær</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
          <div>{lbl("Topp")}<select style={inp()} value={profile.top} onChange={e=>setP("top",e.target.value)}><option value="">–</option>{["XS","S","M","L","XL","XXL"].map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div>{lbl("Bukse")}<select style={inp()} value={profile.pants} onChange={e=>setP("pants",e.target.value)}><option value="">–</option>{["XS","S","M","L","XL","XXL"].map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div>{lbl("Sko")}<input style={inp()} type="number" value={profile.shoes} onChange={e=>setP("shoes",e.target.value)} placeholder="38"/></div>
        </div>
        <div style={{fontSize:11,color:C.muted,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Kostymemål (cm)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          <div>{lbl("Byste")}<input style={inp()} type="number" value={profile.bust} onChange={e=>setP("bust",e.target.value)} placeholder="90"/></div>
          <div>{lbl("Midje")}<input style={inp()} type="number" value={profile.waist} onChange={e=>setP("waist",e.target.value)} placeholder="70"/></div>
          <div>{lbl("Hofte")}<input style={inp()} type="number" value={profile.hips} onChange={e=>setP("hips",e.target.value)} placeholder="95"/></div>
          <div>{lbl("Innsøm")}<input style={inp()} type="number" value={profile.inseam} onChange={e=>setP("inseam",e.target.value)} placeholder="78"/></div>
        </div>
        <button style={btn("primary")} onClick={saveProfile}>Lagre endringer</button>
      </div>
      <div style={{...card(),marginBottom:16}}>
        {secTitle("Varslingsinnstillinger")}
        <div style={{fontSize:11,color:C.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:0.5}}>Kanal</div>
        {[{key:"inApp",label:"Varsler i appen",desc:"Bjellen i menyen"},{key:"email",label:"E-post",desc:email}].map(({key,label,desc})=>(
          <div key={key} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 0",borderBottom:`0.5px solid ${C.border}`}}>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500}}>{label}</div><div style={{fontSize:11,color:C.muted,marginTop:2}}>{desc}</div></div>
            <Toggle on={prefs[key]} onClick={()=>togglePref(key)}/>
          </div>
        ))}
        <div style={{fontSize:11,color:C.muted,margin:"16px 0 8px",textTransform:"uppercase",letterSpacing:0.5}}>Varsle meg når</div>
        {[{key:"onInvite",label:"Jeg inviteres til en jobb",icon:"✉"},{key:"onConfirmed",label:"En jobb bekreftes",icon:"✓"},{key:"onUpdated",label:"Et event oppdateres",icon:"↻"},{key:"onReminder",label:"Påminnelse om ubesvarte invitasjoner",icon:"!"}].map(({key,label,icon})=>(
          <div key={key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`0.5px solid ${C.border}`}}>
            <div style={{width:26,height:26,borderRadius:"50%",background:`${C.orange}22`,border:`0.5px solid ${C.orange}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:C.orange,flexShrink:0}}>{icon}</div>
            <div style={{flex:1,fontSize:13}}>{label}</div>
            <Toggle on={prefs[key]} onClick={()=>togglePref(key)}/>
          </div>
        ))}
        <div style={{marginTop:14}}><button style={btn("primary")} onClick={savePrefs}>Lagre innstillinger</button></div>
      </div>
<div style={card()}>
        {secTitle("Endre passord")}
        <div style={{marginBottom:14}}>{lbl("Nytt passord")}<input style={inp()} type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Minst 6 tegn"/></div>
        <div style={{marginBottom:16}}>{lbl("Bekreft nytt passord")}<input style={inp()} type="password" value={confPw} onChange={e=>setConfPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&savePassword()}/></div>
        <button style={btn("primary")} onClick={savePassword}>Endre passord</button>
      </div>
      {isMobile&&<div style={{...card({marginTop:16,border:`0.5px solid ${C.red}33`})}}>
        <button style={{...btn("danger"),width:"100%",padding:12}} onClick={async()=>{ await supabase.auth.signOut(); window.location.reload(); }}>Logg ut</button>
      </div>}
    </div>
  );
}

// ── Feedback ──
function FeedbackModal({ user, currentPage, onClose }) {
  const [message,setMessage]=useState("");
  const [type,setType]=useState("bug");
  const [sending,setSending]=useState(false);
  const [done,setDone]=useState(false);

  const send = async () => {
    if(!message.trim()) return;
    setSending(true);
    await supabase.from('feedback').insert({
      user_id: user.id,
      user_name: user.name,
      message,
      type,
      page: currentPage,
      status: 'open'
    });
    setSending(false);
    setDone(true);
    setTimeout(()=>onClose(), 2000);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:16}}>
      <div style={{background:C.surface,borderRadius:12,padding:24,width:"100%",maxWidth:420,border:`0.5px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:16,fontWeight:500}}>Tilbakemelding / Feil</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        {done
          ? <div style={{textAlign:"center",padding:20}}>
              <div style={{fontSize:24,color:C.green,marginBottom:8}}>✓</div>
              <div style={{fontSize:14,color:C.white}}>Takk! Vi ser på det.</div>
            </div>
          : <>
              <div style={{display:"flex",gap:8,marginBottom:16}}>
                {[{k:"bug",l:"Feil"},{k:"feedback",l:"Tilbakemelding"},{k:"suggestion",l:"Forslag"}].map(({k,l})=>(
                  <div key={k} onClick={()=>setType(k)} style={{flex:1,padding:"7px 0",borderRadius:8,textAlign:"center",cursor:"pointer",fontSize:12,border:`0.5px solid ${type===k?C.orange:C.border}`,background:type===k?"#FF5F1F22":"transparent",color:type===k?C.orange:C.muted}}>{l}</div>
                ))}
              </div>
              <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>Melding</div>
              <textarea style={{background:C.surface2,border:`0.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",color:C.white,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box",minHeight:100,resize:"vertical",marginBottom:16}} placeholder="Beskriv feilen eller tilbakemeldingen din..." value={message} onChange={e=>setMessage(e.target.value)}/>
              <button style={{width:"100%",padding:11,borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer",border:"none",background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}} onClick={send} disabled={sending}>{sending?"Sender...":"Send"}</button>
            </>}
      </div>
    </div>
  );
}

// ── Feedback Admin ──
function FeedbackAdminPage({ user, users, isMobile, onCountChange, onNotify }) {
  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [filter,setFilter]=useState("all");
  const [notes,setNotes]=useState({});

useEffect(()=>{
    const loadFeedback = async () => {
      const { data } = await supabase.from('feedback').select('*').order('created_at',{ascending:false});
      if(data) {
        setItems(data);
        onCountChange?.(data.filter(i=>i.status==="open").length);
      }
      setLoading(false);
    };
    loadFeedback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const updateCount = (updatedItems) => {
    onCountChange?.(updatedItems.filter(i=>i.status==="open").length);
  };

  const toggleFixed = async (item) => {
    const newStatus = item.status==="fixed" ? "open" : "fixed";
    const { error } = await supabase.from('feedback').update({status:newStatus}).eq('id',item.id);
    if(!error) {
      const updated = items.map(i=>i.id===item.id?{...i,status:newStatus}:i);
      setItems(updated);
      updateCount(updated);
       if(newStatus==="fixed"&&onNotify) {
        const recipient = users.find(u=>u.id===item.user_id);
        if(recipient&&recipient.id!==user.id) onNotify({
          type:"feedback_fixed",
          project_id:null,
          project_name:null,
          recipient_ids:[recipient.id],
          read:{},
          feedback_message:item.message.slice(0,60)
        });
      }
    }
  };

  const setPriority = async (id, priority) => {
    const { error } = await supabase.from('feedback').update({priority}).eq('id',id);
    if(!error) setItems(prev=>prev.map(i=>i.id===id?{...i,priority}:i));
  };

  const saveNote = async (id) => {
    const note = notes[id];
    if(note===undefined) return;
    const { error } = await supabase.from('feedback').update({admin_note:note}).eq('id',id);
    if(!error) setItems(prev=>prev.map(i=>i.id===id?{...i,admin_note:note}:i));
  };

  const typeColors = { bug:C.red, feedback:C.blue, suggestion:C.gold };
  const typeLabels = { bug:"Feil", feedback:"Tilbakemelding", suggestion:"Forslag" };
  const priorityColors = { high:C.red, normal:C.orange, low:C.muted };
  const priorityLabels = { high:"Høy", normal:"Normal", low:"Lav" };

  const allFiltered = filter==="all" ? items : items.filter(i=>i.type===filter||i.status===filter);
  const sorted = [
    ...allFiltered.filter(i=>i.status!=="fixed"),
    ...allFiltered.filter(i=>i.status==="fixed"),
  ];
  const openCount = items.filter(i=>i.status==="open").length;
  const fmtDate = d => new Date(d+"Z").toLocaleDateString("nb-NO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:500}}>Tilbakemeldinger</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {openCount>0&&<div style={{fontSize:13,color:C.red,padding:"3px 10px",borderRadius:20,background:`${C.red}22`,border:`0.5px solid ${C.red}44`}}>{openCount} åpne</div>}
        </div>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:20,overflowX:"auto",paddingBottom:4}}>
        {[{k:"all",l:"Alle"},{k:"bug",l:"Feil"},{k:"feedback",l:"Tilbakemeldinger"},{k:"suggestion",l:"Forslag"},{k:"open",l:"Åpne"},{k:"fixed",l:"Fikset"}].map(({k,l})=>(
          <button key={k} onClick={()=>setFilter(k)} style={{padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${filter===k?C.orange:C.border}`,background:filter===k?"#FF5F1F22":"transparent",color:filter===k?C.orange:C.muted,whiteSpace:"nowrap",flexShrink:0}}>{l}</button>
        ))}
      </div>

      {loading&&<div style={{color:C.muted,fontSize:14,textAlign:"center",marginTop:40}}>Laster...</div>}
      {!loading&&sorted.length===0&&<div style={{color:C.muted,fontSize:14,textAlign:"center",marginTop:40}}>Ingen tilbakemeldinger.</div>}

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
      {sorted.map(item=>(
        <div key={item.id} style={{...card({marginBottom:0,opacity:item.status==="fixed"?0.5:1,borderColor:item.status==="fixed"?C.border:`${typeColors[item.type]||C.muted}44`})}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <div onClick={()=>toggleFixed(item)} style={{width:20,height:20,borderRadius:5,border:`1.5px solid ${item.status==="fixed"?C.green:C.border}`,background:item.status==="fixed"?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginTop:2,transition:"all 0.15s"}}>
              {item.status==="fixed"&&<svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#000" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,flexWrap:"wrap",gap:6}}>
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:`${typeColors[item.type]||C.muted}22`,color:typeColors[item.type]||C.muted,border:`0.5px solid ${typeColors[item.type]||C.muted}44`}}>{typeLabels[item.type]||item.type}</div>
                  {item.priority&&<div style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:`${priorityColors[item.priority]}22`,color:priorityColors[item.priority],border:`0.5px solid ${priorityColors[item.priority]}44`}}>{priorityLabels[item.priority]}</div>}
                  {item.status==="fixed"&&<div style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:`${C.green}22`,color:C.green,border:`0.5px solid ${C.green}44`}}>✓ Fikset</div>}
                </div>
                <div style={{fontSize:11,color:C.muted}}>{fmtDate(item.created_at)}</div>
              </div>
              <div style={{fontSize:12,color:"#777",marginBottom:4}}><span style={{color:"#aaa",fontWeight:500}}>{item.user_name}</span> · <span style={{color:C.orange}}>{item.page}</span></div>
              <div style={{fontSize:13,color:item.status==="fixed"?"#666":"#ccc",lineHeight:1.5,marginBottom:10}}>{item.message}</div>
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                {["high","normal","low"].map(p=>(
                  <button key={p} onClick={()=>setPriority(item.id,p)} style={{padding:"3px 10px",borderRadius:20,fontSize:11,cursor:"pointer",border:`0.5px solid ${item.priority===p?priorityColors[p]:C.border}`,background:item.priority===p?`${priorityColors[p]}22`:"transparent",color:item.priority===p?priorityColors[p]:C.muted}}>
                    {priorityLabels[p]}
                  </button>
                ))}
                <input style={{...inp({flex:1,fontSize:11,padding:"4px 10px",minWidth:100}),marginLeft:4}} placeholder="Notat..." value={notes[item.id]??item.admin_note??""} onChange={e=>setNotes({...notes,[item.id]:e.target.value})} onKeyDown={e=>e.key==="Enter"&&saveNote(item.id)}/>
                <button style={btn("ghost",{padding:"4px 10px",fontSize:11})} onClick={()=>saveNote(item.id)}>Lagre</button>
              </div>
            </div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}

// ── Admin Todos ──
function AdminTodosPage({ projects, onUpdateProject, isMobile }) {
  const [editingId,setEditingId]=useState(null);
  const [editTitle,setEditTitle]=useState("");
  const [editDeadline,setEditDeadline]=useState("");
  const [showDone,setShowDone]=useState(false);

  const allTodos = projects
    .filter(p=>!p.archived&&(p.todos||[]).length>0)
    .flatMap(p=>(p.todos||[]).map(t=>({...t,projectName:p.name,projectId:p.id,project:p})))
    .filter(t=>!t.done)
    .sort((a,b)=>{ if(!a.deadline&&!b.deadline) return 0; if(!a.deadline) return 1; if(!b.deadline) return -1; return new Date(a.deadline)-new Date(b.deadline); });

  const byProject = projects.filter(p=>!p.archived&&(p.todos||[]).length>0);

  const toggleTodo = (project, todoId) => {
    const updated = {...project, todos:(project.todos||[]).map(t=>t.id===todoId?{...t,done:!t.done}:t)};
    onUpdateProject(updated);
  };

  const startEdit=(t,p)=>{ setEditingId(t.id); setEditTitle(t.title); setEditDeadline(t.deadline||""); };
  const saveEdit=(project)=>{
    if(!editTitle.trim()) return;
    const updated={...project,todos:(project.todos||[]).map(t=>t.id===editingId?{...t,title:editTitle,deadline:editDeadline}:t)};
    onUpdateProject(updated);
    setEditingId(null);
  };

  const fmtDate = d => new Date(d+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short",year:"numeric"});
  const isOverdue = d => d && new Date(d+"T23:59:59") < new Date();
  const isDueSoon = d => { if(!d) return false; const diff=new Date(d+"T23:59:59")-new Date(); return diff>0&&diff<3*24*60*60*1000; };

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:500}}>To-dos</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{fontSize:13,color:C.muted}}>{allTodos.length} åpne</div>
          <button style={btn("ghost",{fontSize:12,padding:"4px 10px"})} onClick={()=>setShowDone(v=>!v)}>{showDone?"Skjul ferdige":"Vis ferdige"}</button>
        </div>
      </div>

      {allTodos.length===0&&<div style={{...card({textAlign:"center",padding:36})}}>
        <div style={{fontSize:14,color:C.muted}}>Ingen åpne to-dos!</div>
      </div>}

      {byProject.map(p=>{
        const todos = (p.todos||[]).filter(t=>showDone?true:!t.done);
        if(todos.length===0) return null;
        return (
          <div key={p.id} style={{...card({marginBottom:16})}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:12,color:C.orange}}>{p.name}</div>
            {todos.map(t=>(
              <div key={t.id} style={{marginBottom:8}}>
                {editingId===t.id
                  ? <div style={{padding:"10px 12px",background:C.surface2,borderRadius:8,border:`0.5px solid ${C.orange}44`}}>
                      <div style={{display:"flex",gap:8,marginBottom:8}}>
                        <input style={inp({flex:1})} value={editTitle} onChange={e=>setEditTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveEdit(p)} autoFocus/>
                        <input style={inp({width:130,flexShrink:0})} type="date" value={editDeadline} onChange={e=>setEditDeadline(e.target.value)}/>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button style={btn("primary",{padding:"5px 14px",fontSize:12})} onClick={()=>saveEdit(p)}>Lagre</button>
                        <button style={btn("ghost",{padding:"5px 14px",fontSize:12})} onClick={()=>setEditingId(null)}>Avbryt</button>
                      </div>
                    </div>
                  : <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:C.surface2,borderRadius:8,opacity:t.done?0.5:1}}>
                      <div onClick={()=>toggleTodo(p,t.id)} style={{width:18,height:18,borderRadius:4,border:`1.5px solid ${t.done?C.green:C.border}`,background:t.done?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginTop:1}}>
                        {t.done&&<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#000" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,textDecoration:t.done?"line-through":"none",color:t.done?C.muted:C.white}}>{t.title}</div>
                        {t.deadline&&<div style={{fontSize:11,marginTop:3,color:isOverdue(t.deadline)?C.red:isDueSoon(t.deadline)?C.gold:C.muted}}>
                          {isOverdue(t.deadline)?"⚠ Utgått: ":isDueSoon(t.deadline)?"⏰ Snart: ":""}{fmtDate(t.deadline)}
                        </div>}
                      </div>
                      {t.createdByInitials&&<div style={{fontSize:10,color:C.muted,flexShrink:0,padding:"0 4px"}}>{t.createdByInitials}</div>}
                      <button onClick={()=>startEdit(t,p)} style={{background:"none",border:"none",color:"#555",cursor:"pointer",fontSize:13,flexShrink:0,padding:"0 4px"}} title="Rediger">✎</button>
                    </div>}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
// ── Admin Stats ──
function AdminStatsPage({ projects, users, isMobile }) {
  const activeProjects = projects.filter(p=>!p.archived&&!isPast(p));
  const pastProjects = projects.filter(p=>isPast(p)&&!p.archived);
  const thisYear = new Date().getFullYear();
  const thisYearProjects = projects.filter(p=>p.date&&new Date(p.date).getFullYear()===thisYear);
  const confirmed = projects.filter(p=>p.status==="confirmed"&&!isPast(p));
  const cancelled = projects.filter(p=>p.status==="cancelled");
  const activeUsers = users.filter(u=>u.active);

  const months = ["Jan","Feb","Mar","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Des"];
  const perMonth = months.map((_,i)=>thisYearProjects.filter(p=>new Date(p.date).getMonth()===i).length);
  const maxVal = Math.max(...perMonth,1);

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{fontSize:20,fontWeight:500,marginBottom:20}}>Statistikk</div>
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:12,marginBottom:24}}>
        {[
          {label:"Aktive eventer",val:activeProjects.length,sub:`${confirmed.length} bekreftet`,color:C.green},
          {label:"Gjennomførte i år",val:pastProjects.filter(p=>new Date(p.date).getFullYear()===thisYear).length,sub:`av ${thisYearProjects.length} totalt`,color:C.orange},
          {label:"Kansellerte",val:cancelled.length,sub:"totalt",color:C.red},
          {label:"Aktive brukere",val:activeUsers.length,sub:`av ${users.length} totalt`,color:C.magenta},
          {label:"Dansere",val:users.filter(u=>u.role==="danser"&&u.active).length,sub:"aktive",color:C.magenta},
          {label:"Musikere",val:users.filter(u=>u.role==="musiker"&&u.active).length,sub:"aktive",color:C.blue},
        ].map(s=>(
          <div key={s.label} style={card()}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:22,fontWeight:600,color:s.color}}>{s.val}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>{s.sub}</div>
          </div>
        ))}
      </div>
      {secTitle(`Eventer per måned – ${thisYear}`)}
      <div style={{...card(),marginBottom:24}}>
        <div style={{display:"flex",alignItems:"flex-end",gap:isMobile?4:8,height:100}}>
          {perMonth.map((val,i)=>(
            <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{fontSize:10,color:C.orange}}>{val>0?val:""}</div>
              <div style={{width:"100%",borderRadius:4,background:val>0?`linear-gradient(180deg,${C.orange},${C.magenta})`:`${C.border}`,height:`${Math.max((val/maxVal)*70,val>0?8:2)}px`,transition:"height 0.3s"}}/>
              <div style={{fontSize:9,color:C.muted,textAlign:"center"}}>{months[i]}</div>
            </div>
          ))}
        </div>
      </div>
      {secTitle("Kommende eventer")}
      {activeProjects.sort((a,b)=>new Date(a.date)-new Date(b.date)).slice(0,5).map(p=>(
        <div key={p.id} style={{...card({marginBottom:8}),display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><div style={{fontSize:13,fontWeight:500}}>{p.name}</div><div style={{fontSize:11,color:C.muted,marginTop:2}}>{new Date(p.date+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short",year:"numeric"})}</div></div>
          <div style={badge(p.status)}>{statusCfg[p.status].label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Admin Users Overview ──
function AdminUsersPage({ users, isMobile }) {
  const hasProfile = u => {
    const p = u.profile_info||{};
    return p.phone&&p.top&&p.bust;
  };
  const complete = users.filter(u=>u.active&&hasProfile(u));
  const missing = users.filter(u=>u.active&&!hasProfile(u));

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      {gradBar}
      <div style={{fontSize:20,fontWeight:500,marginBottom:8}}>Brukeroversikt</div>
      <div style={{fontSize:13,color:C.muted,marginBottom:20}}>{complete.length} av {users.filter(u=>u.active).length} har fylt ut profilinformasjon</div>
      <div style={{...card({marginBottom:20})}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
          <div style={{flex:1,height:8,borderRadius:4,background:C.border,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:4,background:`linear-gradient(90deg,${C.orange},${C.magenta})`,width:`${users.filter(u=>u.active).length>0?(complete.length/users.filter(u=>u.active).length)*100:0}%`,transition:"width 0.5s"}}/>
          </div>
          <div style={{fontSize:12,color:C.orange,flexShrink:0}}>{users.filter(u=>u.active).length>0?Math.round((complete.length/users.filter(u=>u.active).length)*100):0}%</div>
        </div>
      </div>
      {missing.length>0&&<>
        {secTitle(`Mangler profilinfo (${missing.length})`)}
        {missing.map(u=>(
          <div key={u.id} style={{...card({marginBottom:8}),display:"flex",alignItems:"center",gap:12}}>
            <Avatar initials={u.initials} size={34} role={u.role}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:500}}>{u.name}</div>
              <div style={{fontSize:11,color:C.muted}}>{u.email}</div>
            </div>
            <span style={rbadge(u.role)}>{roleCfg[u.role]?.label}</span>
          </div>
        ))}
      </>}
      {complete.length>0&&<>
        {secTitle(`Profil komplett (${complete.length})`)}
        {complete.map(u=>(
          <div key={u.id} style={{...card({marginBottom:8}),display:"flex",alignItems:"center",gap:12,opacity:0.7}}>
            <Avatar initials={u.initials} size={34} role={u.role}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:500}}>{u.name}</div>
              <div style={{fontSize:11,color:C.muted}}>{u.email}</div>
            </div>
            <div style={{fontSize:11,color:C.green}}>✓</div>
          </div>
        ))}
      </>}
    </div>
  );
}

// ── Bottom nav (mobile) ──
// eslint-disable-next-line no-unused-vars
const NAV = [
  { id:"dashboard",   label:"Hjem",       icon:(active)=><svg width="20" height="20" viewBox="0 0 16 16" fill={active?"currentColor":"none"} stroke="currentColor" strokeWidth="1.5"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg> },
  { id:"projects",    label:"Eventer", icon:(active)=><svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={active?2:1.5}><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M5 1v4M11 1v4M2 7h12"/></svg> },
  { id:"calendar",    label:"Kalender",   icon:(active)=><svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={active?2:1.5}><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M5 1v4M11 1v4M2 7h12"/><circle cx="8" cy="10" r="1.5" fill={active?"currentColor":"none"} stroke="currentColor"/></svg> },
  { id:"invitations", label:"Invitert",   icon:(active)=><svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={active?2:1.5}><path d="M2 4h12v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4z"/><path d="M2 4l6 5 6-5"/></svg> },
  { id:"profile",     label:"Profil",     icon:(active)=><svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={active?2:1.5}><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg> },
];
const NAV_DESKTOP = [
  { id:"dashboard",   label:"Dashboard",    icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg> },
  { id:"projects",    label:"Eventer",   icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M5 1v4M11 1v4M2 7h12"/></svg> },
  { id:"calendar",    label:"Kalender",     icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M5 1v4M11 1v4M2 7h12"/><circle cx="8" cy="10" r="1.5" fill="currentColor" stroke="none"/></svg> },
  { id:"invitations", label:"Invitasjoner", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4h12v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4z"/><path d="M2 4l6 5 6-5"/></svg> },
  { id:"team",        label:"Team",         icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6" cy="5" r="3"/><path d="M1 13c0-3 2-4 5-4s5 1 5 4"/><path d="M11 8v6M14 11h-6"/></svg>, adminOnly:true, adminSub:true },
  { id:"admin-stats", label:"Statistikk", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 12V8M6 12V4M10 12V6M14 12V2"/></svg>, adminOnly:true, adminSub:true },
  { id:"feedback-admin", label:"Tilbakemeldinger", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2h12v10H9l-3 2v-2H2z"/></svg>, adminOnly:true, feedbackBadge:true, adminSub:true },
  { id:"admin-users", label:"Brukeroversikt", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M12 8l2 2-2 2"/></svg>, adminOnly:true, adminSub:true },
  { id:"broadcast", label:"Meldinger", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2h12v10H9l-3 2v-2H2z"/><path d="M5 6h6M5 8h4"/></svg>, adminOnly:true, adminSub:true },
  { id:"admin-todos", label:"To-dos", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 4h8M6 8h8M6 12h8"/><circle cx="2.5" cy="4" r="1"/><circle cx="2.5" cy="8" r="1"/><circle cx="2.5" cy="12" r="1"/></svg>, adminOnly:true, adminSub:true },
  { id:"activity", label:"Aktivitet", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 8h2l2-5 3 10 2-7 2 4 1-2h2"/></svg>, adminOnly:true, adminSub:true },
  { id:"repertoire", label:"Repertoire", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2h12v12H2z"/><path d="M5 6h6M5 9h4"/></svg>, adminOnly:true, adminSub:true },
  { id:"portals", label:"Kundeportaler", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 3h12v10H2z"/><path d="M2 6h12"/><circle cx="4" cy="4.5" r="0.5" fill="currentColor"/></svg>, adminOnly:true, adminSub:true },
  { id:"customers", label:"Kunder", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg>, adminOnly:true, adminSub:true },
  { id:"offers", label:"Tilbud", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 2h7l3 3v9H3z"/><path d="M9 2v4h4"/><path d="M6 9h4M6 11h4"/></svg>, adminOnly:true, adminSub:true },
  { id:"settings", label:"Innstillinger", icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.3 3.3l1.4 1.4M11.3 11.3l1.4 1.4M11.3 3.3l-1.4 1.4M4.7 11.3l-1.4 1.4"/></svg>, adminOnly:true, adminSub:true },
  { id:"profile",     label:"Min profil",   icon:<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg> },
];

// ── App ──
export default function App() {
  const isMobile = useIsMobile();
  const [users,setUsers]=useState([]);
  const [user,setUser]=useState(null);
  const [page,setPage]=useState("dashboard");
  const [projects,setProjects]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null);
  const [showNew,setShowNew]=useState(false);
  const [notifications,setNotifications]=useState([]);
  const [showNotif,setShowNotif]=useState(false);
  const [showFeedback,setShowFeedback]=useState(false);
  const [unreadFeedback,setUnreadFeedback]=useState(0);
  const [showMobileMenu,setShowMobileMenu]=useState(false);
  const [adminMenuOpen,setAdminMenuOpen]=useState(false);
  const [availability,setAvailability]=useState([]);
  const [appSettings,setAppSettings]=useState({allow_artist_availability:true});
  const [customers,setCustomers]=useState([]);


useEffect(()=>{
supabase.auth.onAuthStateChange(async (event) => {
  if(event==="PASSWORD_RECOVERY") {
    setPage("reset-password");
  }
});
const fetchData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const [usersRes, projectsRes, notifRes, feedbackRes, availRes, settingsRes, customersRes] = await Promise.all([
      supabase.from('users').select('*').order('name'),
      supabase.from('projects').select('*').order('date'),
      supabase.from('notifications').select('*').order('created_at', {ascending:false}),
      supabase.from('feedback').select('id,status'),
      supabase.from('availability').select('*').order('start_date'),
      supabase.from('settings').select('*'),
      supabase.from('customers').select('*').order('name'),
    ]);
    const usersData = usersRes.data||[];
    const projectsData = projectsRes.data||[];
    const notifData = notifRes.data||[];
    const feedbackData = feedbackRes.data||[];
    const availData = availRes.data||[];
    const settingsData = settingsRes?.data||[];
    setCustomers(customersRes?.data||[]);
    if(settingsData.length>0) setAppSettings(Object.fromEntries(settingsData.map(r=>[r.key,r.value])));

    // Vent til vi faktisk har data før vi viser appen
    if(usersData.length>0) setUsers(usersData);
    if(projectsData.length>0) setProjects(projectsData);
    if(notifData.length>0) setNotifications(notifData);
    setUnreadFeedback(feedbackData.filter(f=>f.status==="open").length);
    setAvailability(availData);

    if(session) {
      const userData = usersData.find(u=>u.email===session.user.email);
      if(userData) setUser(userData);
      else {
        // Prøv på nytt hvis bruker ikke ble funnet
        const { data: retryUser } = await supabase.from('users').select('*').eq('email', session.user.email).single();
        if(retryUser) {
          setUser(retryUser);
          if(usersData.length===0) {
            const { data: retryUsers } = await supabase.from('users').select('*').order('name');
            if(retryUsers) setUsers(retryUsers);
          }
        }
      }
    }
    setLoading(false);
  };
fetchData();

  // ── Realtime ──
  const projectsSub = supabase
    .channel('projects-changes')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, payload => {
      if (payload.eventType === 'INSERT') {
        setProjects(ps => ps.some(p => p.id === payload.new.id) ? ps : [...ps, payload.new]);
      } else if (payload.eventType === 'UPDATE') {
        setProjects(ps => ps.map(p => p.id === payload.new.id ? payload.new : p));
        setSelected(sel => sel?.id === payload.new.id ? payload.new : sel);
      } else if (payload.eventType === 'DELETE') {
        setProjects(ps => ps.filter(p => p.id !== payload.old.id));
      }
    })
    .subscribe();

  const notifSub = supabase
    .channel('notifications-changes')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, payload => {
      if (payload.eventType === 'INSERT') {
        setNotifications(ns => ns.some(n => n.id === payload.new.id) ? ns : [payload.new, ...ns]);
      } else if (payload.eventType === 'UPDATE') {
        setNotifications(ns => ns.map(n => n.id === payload.new.id ? payload.new : n));
      } else if (payload.eventType === 'DELETE') {
        setNotifications(ns => ns.filter(n => n.id !== payload.old.id));
      }
    })
    .subscribe();

  const availSub = supabase
    .channel('availability-changes')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'availability' }, payload => {
      if (payload.eventType === 'INSERT') {
        setAvailability(av => av.some(a => a.id === payload.new.id) ? av : [...av, payload.new]);
      } else if (payload.eventType === 'UPDATE') {
        setAvailability(av => av.map(a => a.id === payload.new.id ? payload.new : a));
      } else if (payload.eventType === 'DELETE') {
        setAvailability(av => av.filter(a => a.id !== payload.old.id));
      }
    })
    .subscribe();

  return () => {
    supabase.removeChannel(projectsSub);
    supabase.removeChannel(notifSub);
    supabase.removeChannel(availSub);
  };
},[]);

// ── Push-varsler ──
useEffect(()=>{
  if(!user) return;
  const registerPush = async () => {
    if(!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      const permission = await Notification.requestPermission();
      if(permission !== 'granted') return;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: 'BA3pDS0wnhqkEgmcqmccFYcuBdjHvP2V20g1pwWxs3tA5rmKUI2DrXaeY-NZAcGyCQlhk3oVgDX7lzC0vuWzPlM'
      });
      await supabase.from('push_subscriptions').upsert({
        user_id: user.id,
        subscription: sub.toJSON(),
      }, { onConflict: 'user_id' });
    } catch(e) {
      console.error('Push registration failed:', e);
    }
  };
  registerPush();
},[user]);

if(loading) return (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:C.bg,fontFamily:"'Inter',system-ui,sans-serif"}}>
    <div style={{textAlign:"center",animation:"fadeIn 0.6s ease-in-out"}}>
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        .grad-anim { background:linear-gradient(90deg,${C.orange},${C.magenta},${C.gold},${C.orange}); background-size:300% 300%; animation:shimmer 2s ease infinite; }
      `}</style>
      <img src="/logo.svg" alt="Royal" style={{width:180,marginBottom:24,opacity:0.95}}/>
      <div className="grad-anim" style={{height:2,borderRadius:1,marginBottom:20}}/>
      <div style={{display:"flex",justifyContent:"center",gap:6}}>
        {[0,1,2].map(i=>(
          <div key={i} style={{width:6,height:6,borderRadius:"50%",background:C.orange,opacity:0.8,animation:`fadeIn 0.6s ease-in-out ${i*0.2}s both`}}/>
        ))}
      </div>
    </div>
  </div>
);
if(!user) return <LoginScreen onLogin={async(userData)=>{
  const [usersRes, projectsRes, notifRes] = await Promise.all([
    supabase.from('users').select('*').order('name'),
    supabase.from('projects').select('*').order('date'),
    supabase.from('notifications').select('*').order('created_at',{ascending:false}),
  ]);
  if(usersRes.data&&usersRes.data.length>0) setUsers(usersRes.data);
  if(projectsRes.data) setProjects(projectsRes.data);
  if(notifRes.data) setNotifications(notifRes.data);
  setUser(userData);
}}/>;

  const goTo = (p) => {
    setPage(p);
    setSelected(null);
    setShowNotif(false);
    const pageLabels = {
      dashboard:"Dashboard", projects:"Eventer", calendar:"Kalender",
      invitations:"Invitasjoner", team:"Team", stats:"Statistikk",
      profile:"Profil", broadcast:"Kringkasting", feedback:"Feedback",
      settings:"Innstillinger", activity:"Aktivitet",
    };
    if(user) logActivity(user, 'page_view', { page_label: pageLabels[p] || p }, p);
  };
  const selectProject=async p=>{
  setPage("detail");
  logActivity(user, 'event_opened', { event_name: p.name }, 'detail');
  const { data } = await supabase.from('projects').select('*').eq('id', p.id).single();
  setSelected(data || p);
};
  const selectProjectById=id=>{ const p=projects.find(x=>x.id===id); if(p) selectProject(p); };

  const updateProject = async (updated) => {
    await supabase.from('projects').upsert(updated);
    setProjects(ps=>ps.map(p=>p.id===updated.id?updated:p));
    setSelected(updated);
  };

  // Kunder
  const reloadCustomersProjects = async () => {
    const [cRes, pRes] = await Promise.all([
      supabase.from('customers').select('*').order('name'),
      supabase.from('projects').select('*').order('date'),
    ]);
    if(cRes.data) setCustomers(cRes.data);
    if(pRes.data) setProjects(pRes.data);
  };
  // Rask opprett brukt av CustomerSelect (returnerer den nye kunden)
  const addCustomer = async (name) => {
    const { data, error } = await supabase.from('customers')
      .insert({ name:name.trim(), created_by:user?.id||null }).select().single();
    if(error){ console.error("addCustomer:",error); return null; }
    setCustomers(cs=>[...cs, data].sort((a,b)=>a.name.localeCompare(b.name,"nb")));
    return data;
  };

const addProject = async (p) => {
  await supabase.from('projects').insert(p);
  setProjects(ps=>[...ps,p]);
  // Send invitasjons-varsler til alle på teamet
  const recipients = (p.team||[]).filter(id=>id!==user.id);
if(recipients.length>0) {
    await addNotification(createNotification("invited", p, recipients), [...projects, p]);
  }
  setShowNew(false);
  goTo("projects");
};

  const deleteProject = async (id) => {
  await supabase.from('notifications').delete().eq('project_id', id);
  await supabase.from('projects').delete().eq('id', id);
  setProjects(ps=>ps.filter(p=>p.id!==id));
  setNotifications(ns=>ns.filter(n=>n.project_id!==id));
  goTo("projects");
};

const updateUsers = async (updated) => {
  const prev = users;
  const changed = updated.find(u => {
    const old = prev.find(x=>x.id===u.id);
    return JSON.stringify(u) !== JSON.stringify(old);
  }) || updated[updated.length-1];
  await supabase.from('users').upsert(changed);
  setUsers(updated);
  if(user) setUser(updated.find(u=>u.id===user.id)||user);
};

const addNotification = async (n, freshProjects) => {
  const { data } = await supabase.from('notifications').insert(n).select().single();
  if(data) setNotifications(ns=>[data,...ns]);

  // Send e-postvarsler til mottakere
  const recipients = (n.recipient_ids||[]).filter(Boolean);
  for(const uid of recipients) {
    const u = users.find(x=>x.id===uid);
    if(!u||!u.email) continue;
    const defaultPrefs={inApp:true,email:true,sms:false,onInvite:true,onConfirmed:true,onUpdated:false,onReminder:false};
    const prefs=u.notif_prefs||defaultPrefs;
    if(n.type==="invited"&&prefs.onInvite===false) continue;
    if(n.type==="confirmed"&&prefs.onConfirmed===false) continue;
    if(n.type==="updated"&&prefs.onUpdated===false) continue;
    if(n.type==="removed") continue;
    if(prefs.email===false) continue;
    const allProjects = freshProjects||projects;
    const proj = allProjects.find(p=>p.id===n.project_id);
    const dato = proj?.date ? new Date(proj.date+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"long",year:"numeric"}) : "";
    await supabase.functions.invoke("send-notification", {
      body: {
        type: n.type,
        project_name: n.project_name,
        project_date: dato,
        recipient_email: u.email,
        recipient_name: u.name,
      }
    });
  }
};
  const markRead = async (id) => {
  const notif = notifications.find(n=>n.id===id);
  if(!notif||notif.read?.[user.id]) return;
  const updatedRead = {...notif.read, [user.id]:true};
  await supabase.from('notifications').update({read:updatedRead}).eq('id',id);
  setNotifications(ns=>ns.map(n=>n.id===id?{...n,read:updatedRead}:n));
};

const pendingInvites=user?projects.filter(p=>p.team.includes(user.id)&&p.invites?.[user.id]==="pending").length:0;

  const isActive=id=>page===id||(page==="detail"&&id==="projects");

  const renderPage=()=>{
if(!user) return <LoginScreen onLogin={setUser}/>;
if(page==="reset-password") return <ResetPasswordScreen onDone={()=>{ setPage("dashboard"); }}/>;
    if(loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"50vh",color:C.muted,fontSize:14}}>Laster...</div>;
    if(page==="detail"&&selected) return <ProjectDetail project={selected} availability={availability} user={user} users={users} customers={customers} onCreateCustomer={addCustomer} onBack={()=>goTo("projects")} onUpdate={updateProject} onDelete={deleteProject} onNotify={addNotification} isMobile={isMobile}/>;
    if(page==="projects") return <ProjectsPage user={user} projects={projects} users={users} onSelect={selectProject} onNew={()=>setShowNew(true)} isMobile={isMobile}/>;
    if(page==="calendar") return <CalendarPage user={user} projects={projects} users={users} onSelect={selectProject} availability={availability} onAvailabilityChange={setAvailability} settings={appSettings} isMobile={isMobile}/>;
    if(page==="invitations") return user.role==="admin"
    ?<AdminInvitationsPage projects={projects} users={users} onUpdateProject={updateProject} onSelectProject={selectProject} isMobile={isMobile}/>
     :<InvitationsPage user={user} projects={projects} users={users} onUpdate={updateProject} onSelectProject={selectProject} isMobile={isMobile}/>;
    if(page==="team") return <TeamPage user={user} users={users} onUpdateUsers={updateUsers} isMobile={isMobile}/>;
    if(page==="settings") return <SettingsPage user={user} isMobile={isMobile}/>;
    if(page==="broadcast") return <BroadcastPage user={user} users={users} projects={projects} onNotify={addNotification} isMobile={isMobile}/>;
    if(page==="feedback-admin") return <FeedbackAdminPage user={user} users={users} isMobile={isMobile} onCountChange={setUnreadFeedback} onNotify={addNotification}/>;
    if(page==="admin-stats") return <AdminStatsPage projects={projects} users={users} isMobile={isMobile}/>;
    if(page==="admin-users") return <AdminUsersPage users={users} isMobile={isMobile}/>;
    if(page==="admin-todos") return <AdminTodosPage projects={projects} onUpdateProject={updateProject} isMobile={isMobile}/>;
    if(page==="activity") return <ActivityPage users={users} isMobile={isMobile}/>;
    if(page==="repertoire") return <RepertoirePage isMobile={isMobile}/>;
    if(page==="portals") return <PortalsPage user={user} projects={projects} customers={customers} onCreateCustomer={addCustomer} isMobile={isMobile}/>;
    if(page==="customers") return <CustomersPage customers={customers} projects={projects} user={user} onReload={reloadCustomersProjects} onOpenProject={selectProject} isMobile={isMobile}/>;
    if(page==="offers") return <OffersPage user={user} customers={customers} projects={projects} onCreateCustomer={addCustomer} onOpenProject={selectProject} isMobile={isMobile}/>;
    if(page==="profile") return <ProfilePage user={user} users={users} onUpdateUsers={updateUsers} isMobile={isMobile}/>;
    return <Dashboard user={user} projects={projects} users={users} onSelect={selectProject} onRespond={(p,decision)=>{ updateProject({...p,invites:{...p.invites,[user.id]:decision}}); logActivity(user, decision==="accepted"?"invite_accepted":"invite_declined", {event_name:p.name}, "dashboard"); }} isMobile={isMobile}/>;
  };
// ── Mobile layout ──
  if(isMobile) return (
    <div style={{background:C.bg,minHeight:"100vh",fontFamily:"'Inter',system-ui,sans-serif",color:C.white}}>
      {showNew&&<NewProjectModal users={users} customers={customers} onCreateCustomer={addCustomer} onSave={addProject} onClose={()=>setShowNew(false)} isMobile={isMobile}/>}
      {showNotif&&user&&<NotifPanel notifications={notifications} user={user} projects={projects} onMarkRead={markRead} onClose={()=>setShowNotif(false)} onSelectProject={id=>{selectProjectById(id);}} onRespond={(proj,uid,status)=>{ updateProject({...proj,invites:{...proj.invites,[uid]:status}}); }} isMobile={isMobile}/>}
      {showFeedback&&user&&<FeedbackModal user={user} currentPage={page} onClose={()=>setShowFeedback(false)}/>}

      {/* Hamburger-meny overlay */}
      {showMobileMenu&&<div style={{position:"fixed",inset:0,zIndex:200,display:"flex"}}>
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.6)"}} onClick={()=>setShowMobileMenu(false)}/>
        <div style={{position:"relative",width:260,background:C.sidebar,height:"100%",display:"flex",flexDirection:"column",borderRight:`0.5px solid ${C.border}`,zIndex:1,overflowY:"auto"}}>
          <div style={{paddingLeft:20,paddingRight:20,paddingBottom:16,paddingTop:`calc(20px + env(safe-area-inset-top))`,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`0.5px solid ${C.border}`}}>
            <img src="/logo.svg" alt="Royal" style={{width:110}}/>
            <button onClick={()=>setShowMobileMenu(false)} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
          </div>
          <div style={{padding:"8px 0 8px"}}>
            <SearchBar projects={projects} users={users} user={user} onSelectProject={p=>{selectProject(p);setShowMobileMenu(false);}} onSelectUser={()=>{goTo("team");setShowMobileMenu(false);}} isMobile={true}/>
          </div>
         <div style={{flex:1}}>
            {NAV_DESKTOP.filter(n=>!n.adminOnly||user?.role==="admin").filter(n=>!n.adminSub).map(n=>(
              <div key={n.id} onClick={()=>{goTo(n.id);setShowMobileMenu(false);}} style={{padding:"12px 20px",fontSize:14,color:isActive(n.id)?C.white:C.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:12,justifyContent:"space-between",borderLeft:`2px solid ${isActive(n.id)?C.orange:"transparent"}`,background:isActive(n.id)?C.surface:"transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>{n.icon}{n.label}</div>
                {n.id==="invitations"&&pendingInvites>0&&<div style={{minWidth:18,height:18,borderRadius:9,background:C.orange,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",padding:"0 5px"}}>{pendingInvites}</div>}
              </div>
            ))}
            {user?.role==="admin"&&<>
              <div onClick={()=>setAdminMenuOpen(v=>!v)} style={{padding:"12px 20px",fontSize:14,color:C.muted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 1a7 7 0 100 14A7 7 0 008 1z"/><path d="M8 5v4l2 2"/></svg>
                  Admin
                </div>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{transition:"transform 0.2s",transform:adminMenuOpen?"rotate(180deg)":"rotate(0deg)"}}><path d="M2 3l3 4 3-4"/></svg>
              </div>
              {adminMenuOpen&&NAV_DESKTOP.filter(n=>n.adminSub).map(n=>(
                <div key={n.id} onClick={()=>{goTo(n.id);setShowMobileMenu(false);}} style={{padding:"10px 20px 10px 40px",fontSize:14,color:isActive(n.id)?C.white:C.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:12,justifyContent:"space-between",borderLeft:`2px solid ${isActive(n.id)?C.orange:"transparent"}`,background:isActive(n.id)?C.surface:"transparent"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>{n.icon}{n.label}</div>
                  {n.feedbackBadge&&unreadFeedback>0&&<div style={{minWidth:18,height:18,borderRadius:9,background:C.red,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",padding:"0 5px"}}>{unreadFeedback}</div>}
                </div>
              ))}
            </>}
          </div>
          <div style={{padding:"16px 20px",borderTop:`0.5px solid ${C.border}`}}>
            {user&&<>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <Avatar initials={user.initials} size={34} role={user.role}/>
                <div><div style={{fontSize:13,fontWeight:500}}>{user.name}</div><div style={{fontSize:11,color:C.muted}}>{roleCfg[user.role]?.label}</div></div>
              </div>
              <button onClick={()=>{setShowFeedback(true);setShowMobileMenu(false);}} style={{...btn("ghost"),width:"100%",padding:8,fontSize:12,marginBottom:8}}>Tilbakemelding / Feil</button>
              <button onClick={async()=>{ await supabase.auth.signOut(); setUser(null);setPage("dashboard");setShowMobileMenu(false); }} style={{...btn("ghost"),width:"100%",padding:8,fontSize:12}}>Logg ut</button>
            </>}
          </div>
        </div>
      </div>}

      {/* Header */}
      <div style={{position:"sticky",top:0,zIndex:50,background:C.sidebar,borderBottom:`0.5px solid ${C.border}`,paddingLeft:16,paddingRight:16,paddingBottom:12,paddingTop:`calc(10px + env(safe-area-inset-top))`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button onClick={()=>setShowMobileMenu(true)} style={{background:"none",border:"none",cursor:"pointer",padding:4,color:C.white,display:"flex",flexDirection:"column",gap:4}}>
              <div style={{width:20,height:1.5,background:C.white,borderRadius:1}}/>
              <div style={{width:20,height:1.5,background:C.white,borderRadius:1}}/>
              <div style={{width:20,height:1.5,background:C.white,borderRadius:1}}/>
            </button>
            <img src="/logo.svg" alt="Royal" style={{width:100,display:"block"}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {user&&<NotifBell notifications={notifications} user={user} onOpen={()=>setShowNotif(true)}/>}
          </div>
        </div>
      </div>

      <div style={{padding:16,paddingBottom:32}}>{renderPage()}</div>
    </div>
  );

// ── Desktop layout ──
  return (
    <div style={{background:C.bg,minHeight:"100vh",display:"flex",fontFamily:"'Inter',system-ui,sans-serif",color:C.white,alignItems:"flex-start"}}>
      {showNew&&<NewProjectModal users={users} customers={customers} onCreateCustomer={addCustomer} onSave={addProject} onClose={()=>setShowNew(false)} isMobile={false}/>}
        {showFeedback&&user&&<FeedbackModal user={user} currentPage={page} onClose={()=>setShowFeedback(false)}/>}
      {showNotif&&user&&<NotifPanel notifications={notifications} user={user} projects={projects} onMarkRead={markRead} onClose={()=>setShowNotif(false)} onSelectProject={id=>{selectProjectById(id);setShowNotif(false);}} onRespond={(proj,uid,status)=>{ updateProject({...proj,invites:{...proj.invites,[uid]:status}}); }} isMobile={false}/>}
    <div style={{width:240,background:C.sidebar,display:"flex",flexDirection:"column",gap:2,flexShrink:0,borderRight:`0.5px solid ${C.border}`,position:"sticky",top:0,height:"100vh",overflowY:"auto"}}>
<div style={{padding:"20px 20px 24px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
    <img src="/logo.svg" alt="Royal" style={{width:120,display:"block"}}/>
  </div>
  <div style={{padding:"0 12px 16px"}}>
    <SearchBar projects={projects} users={users} user={user} onSelectProject={p=>{selectProject(p);}} onSelectUser={u=>{goTo("team");}} isMobile={false}/>
  </div>
       {NAV_DESKTOP.filter(n=>!n.adminOnly||user?.role==="admin").filter(n=>!n.adminSub).map(n=>(
          <div key={n.id} style={{padding:"10px 20px",fontSize:13,color:isActive(n.id)?C.white:C.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:10,justifyContent:"space-between",borderLeft:`2px solid ${isActive(n.id)?C.orange:"transparent"}`,background:isActive(n.id)?C.surface:"transparent"}} onClick={()=>goTo(n.id)}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>{n.icon}{n.label}</div>
            {n.id==="invitations"&&pendingInvites>0&&<div style={{minWidth:18,height:18,borderRadius:9,background:C.orange,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",padding:"0 5px"}}>{pendingInvites}</div>}
          </div>
        ))}
        {user?.role==="admin"&&<>
          <div onClick={()=>setAdminMenuOpen(v=>!v)} style={{padding:"10px 20px",fontSize:13,color:C.muted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",borderLeft:`2px solid transparent`}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 1a7 7 0 100 14A7 7 0 008 1z"/><path d="M8 5v4l2 2"/></svg>
              Admin
            </div>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{transition:"transform 0.2s",transform:adminMenuOpen?"rotate(180deg)":"rotate(0deg)"}}><path d="M2 3l3 4 3-4"/></svg>
          </div>
          {adminMenuOpen&&NAV_DESKTOP.filter(n=>n.adminSub).map(n=>(
            <div key={n.id} onClick={()=>goTo(n.id)} style={{padding:"9px 20px 9px 36px",fontSize:13,color:isActive(n.id)?C.white:C.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:10,justifyContent:"space-between",borderLeft:`2px solid ${isActive(n.id)?C.orange:"transparent"}`,background:isActive(n.id)?C.surface:"transparent"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>{n.icon}{n.label}</div>
              {n.feedbackBadge&&unreadFeedback>0&&<div style={{minWidth:18,height:18,borderRadius:9,background:C.red,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",padding:"0 5px"}}>{unreadFeedback}</div>}
            </div>
          ))}
        </>}
<div style={{paddingBottom:160}}/>
<div style={{position:"fixed",bottom:0,left:0,width:240,background:C.sidebar,borderTop:`0.5px solid ${C.border}`,zIndex:10}}>
        <div style={{padding:"16px 20px"}}>
          {user&&<>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <Avatar initials={user.initials} size={30} role={user.role}/>
                <div><div style={{fontSize:12,fontWeight:500}}>{user.name.split(" ")[0]}</div><div style={{fontSize:11,color:C.muted}}>{roleCfg[user.role]?.label}</div></div>
              </div>
              <NotifBell notifications={notifications} user={user} onOpen={()=>setShowNotif(v=>!v)}/>
            </div>
            <div style={{fontSize:10,color:"#333",textAlign:"center",marginBottom:8}}>Utviklet av <span style={{color:"#444"}}>Just a Name</span></div>
            <button onClick={()=>setShowFeedback(true)} style={{...btn("ghost"),width:"100%",padding:7,fontSize:11,marginBottom:6,color:"#555"}}>Tilbakemelding / Feil</button>
                        <button onClick={async()=>{ await supabase.auth.signOut(); setUser(null);setPage("dashboard");setShowNotif(false); }} style={{...btn("ghost"),width:"100%",padding:7,fontSize:12}}>Logg ut</button>
          </>}
        </div>
        </div>
      </div>
      <div style={{flex:1,padding:28,overflowY:"auto"}}>{renderPage()}</div>
    </div>
  );
}